/**
 * FieldRecordModal (Inventory Screens Step 5.2 — Phase 1 chrome, Phase 2
 * editors, Phase 3 PN/AT + Save) — the field zone's per-record modal.
 *
 * Phase 1: ModalShell chrome; router-native location.state open / navigate(-1)
 * close (D-5.2-A). Phase 2: D-5.2-E editors blur/change-committing through
 * fieldStore.updateWorkingField (working + dirty scalar sentinels; baseline
 * and etag untouched, D-4.U); MATERIAL + provenance chip (D-5.2-D).
 *
 * Phase 3 (D-5.2-B/F/H): PartNumbersList/AssetTagsList mounted VERBATIM
 * (callback-pure — zero component edits) with field-zone host handlers:
 *   - children live in the record row's TOP-LEVEL arrays (part_numbers +
 *     extra_* overflow — no baseline/working split for children); handlers
 *     work on the MERGED set and write through fieldStore.updateChildSet
 *     (full set → primary array, extra_* flattened, dirty sentinel true).
 *   - SHAPE MAP at the host: lists speak {part_number, part_number_type,
 *     part_description} / {tag_value}; rows store {value, type} / {value}.
 *     part_description is carried locally but is NOT part of the capture
 *     envelope (buildCapturePayload picks {id?, value, type?} only).
 *   - LOCAL KEY: adds mint local_key = crypto.randomUUID(); list/edit key is
 *     id ?? local_key. buildCapturePayload PICKS its output objects (pre-read
 *     confirmed), so local_key never reaches the wire; id:null = new → the
 *     builder omits id → server mints; reconcile re-keys by value.
 *   - New PN default type TBD at capture; PN/AT values are identifiers —
 *     trim only, never uppercase.
 *   - DUPE GUARD (D-5.2-H): an Add whose trimmed value case-insensitively
 *     matches the current working set is rejected with an inline error.
 * Save (D-5.2-F): ONE enqueueFieldCapture for the record — a local IDB write;
 * the field_capture-scoped coalescer absorbs rapid re-saves; null return =
 * nothing to send. Closing WITHOUT Save keeps all dirt (persists, reopens
 * dirty). No drain trigger changes — replay runs on the zone's normal cycle.
 *
 * 5.3 Phase 1 (D-5.3-8): enum PRESENTATION swap — EnumEditor's <select>
 * became a trigger button opening the single modal-level BottomSheetSelect
 * (oversized daylight rows, addendum §F). Same options source / refKey /
 * commit-if-changed semantics; sheet open rides its own tmgFieldSheetField
 * sentinel spread over the modal's state (back closes the sheet only).
 *
 * 5.3 Phase 2: EDIT-MODE media — the MEDIA count row became
 * FieldMediaGallery (capture → base64 → enqueueMediaAdd, full-res no
 * conversion D-5.3-3, photo_type 'tool_photo' D-5.3-5, optimistic pending
 * entry in row media[] D-5.3-4) + FieldMediaViewer on its own
 * tmgFieldMediaViewerIdx sentinel; delete = D-4.AG pending-cancel else
 * enqueueMediaRemove.
 *
 * 5.3 Phase 3a: CREATE-MODE media — same gallery in buffer mode
 * (D-5.3-P3-1): captures go to the draft-local mediaBuffer (no enqueue —
 * no record_id exists); discard rides the createSession reset (revoke all);
 * Create flushes (D-5.3-P3-2): temp row carries the entries (object_urls
 * moved, not revoked) → tool_create enqueued FIRST → media_adds against the
 * pending-* id, outbox_ids stamped back → buffer cleared. The drain skips
 * pending-*-keyed media_adds (D-5.3-P3-3) until 3b's D.4 re-key.
 */

import React, { useState, useEffect, useLayoutEffect, useCallback, useRef, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useLiveQuery } from 'dexie-react-hooks';
import ModalShell from '../../../shared/components/ModalShell';
import BottomSheetSelect from './BottomSheetSelect';
import FieldComboSheet from './FieldComboSheet';
import FieldMediaGallery from './FieldMediaGallery';
import FieldMediaViewer from './FieldMediaViewer';
import PartNumbersList from '../../../shared/components/PartNumbersList';
import AssetTagsList from '../../../shared/components/AssetTagsList';
import { useFieldReferenceData } from '../data/fieldRefData';
import { recordLabel } from '../recordLabel';
import { fieldDb } from '../db/fieldDb';
import { updateWorkingField, updateChildSet, getRecords, getRecord, ensureOpen } from '../db/fieldStore';
import {
  enqueueFieldCapture, enqueueToolCreate,
  enqueueMediaAdd, enqueueMediaRemove, cancelPendingMediaAdd, getQueue,
  getDiscardPreflight, discardPendingRecord, hasAnyToolCreate,
} from '../sync/fieldOutbox';
import { isPendingRecord } from '../sync/pendingId';
import { findToolIdMatches, toolIdMatchKey, toolIdDuplicateMessage } from '../data/toolIdMatch';
import {
  EDITABLE_FIELDS, MEDIA_AFTER_KEY, READONLY_DISPLAY_FIELDS, fieldsForView,
} from '../data/editableFields';
import { formatReadonly } from '../data/formatReadonly';
import { normalizeForCompare } from '../data/normalizeForCompare';
import { rankByUsage } from '../data/rankByUsage';
import './FieldRecordModal.css';

/* ── Phase 2 editors (unchanged commit path) ─────────────────────────────── */

function TextEditor({ field, record, onCommit, commitOnChange = false, focusOnMount = false }) {
  // P2-5 Phase 3: a duplicate lands with TOOL ID focused and its text selected,
  // so the rep can type over the incremented ID at once. Mount only: the
  // create content remounts per session, so this runs once per duplicate.
  const inputRef = useRef(null);
  useEffect(() => {
    if (focusOnMount && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const current = record.working?.[field.key];
  const [draft, setDraft] = useState(current ?? '');
  useEffect(() => { setDraft(record.working?.[field.key] ?? ''); },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [record.record_id]);

  // F-59b: the commit RULE, parameterised. Body is unchanged apart from
  // reading `raw` instead of `draft` — one definition of trim / number
  // coercion / unchanged-value comparison, reached from two entry points.
  // onChange cannot use `commit` below: setDraft has not landed when it
  // fires, so the editor's own state still holds the PREVIOUS character.
  // Both entry points still end at onCommit — there is no second path to
  // the modal.
  // DIRTY-ON-CLOSE: BOTH sides go through normalizeForCompare. Comparing the
  // cleaned draft against the RAW stored value made a stored '', a number in
  // a text field, padded text or a numeric string read as an edit, so closing
  // an untouched record dirtied it. `next` is unchanged from before — it is
  // what a real edit commits.
  const commitValue = (raw) => {
    const next = normalizeForCompare(raw, field.kind);
    if (next === normalizeForCompare(current, field.kind)) return;
    onCommit(field.key, next);
  };

  // The BLUR entry point, and deliberately ZERO-ARG: onBlur={commit} hands
  // this the blur EVENT, so a defaulted parameter would take that object as
  // the value and commit the string "[object Object]".
  const commit = () => commitValue(draft);

  // A ref onto the CURRENT commit closure. The unmount cleanup below must
  // see the latest `draft`, and a cleanup captured with an empty dep array
  // would otherwise close over the FIRST render's draft — committing an
  // empty string over whatever the rep actually typed. The ref is what makes
  // a once-only effect safe here.
  const commitRef = useRef(commit);
  commitRef.current = commit;

  // Commit on unmount. This editor commits on BLUR only, so an editor torn
  // down while focused with uncommitted typing loses it — removing a focused
  // node does not reliably fire blur. Pre-existing and reachable today: the
  // modal closing on hardware/gesture back mid-type opens the same window.
  //
  // Empty dep array + empty body: React runs this once and the cleanup fires
  // once, at unmount. Nothing here commits per keystroke, and onBlur is
  // untouched — a blurred field has already committed and this is then a
  // no-op by the early return.
  //
  // UNCHANGED VALUE IS A TRUE NO-OP: commit computes `next`, compares it to
  // the normalised stored value, and returns before calling onCommit. No store write, no
  // dirty sentinel, no material_source latch. That is what lets a future
  // view switch unmount editors without dirtying a clean record.
  useEffect(() => () => { commitRef.current(); }, []);

  return (
    <input
      ref={inputRef}
      type={field.kind === 'number' ? 'number' : 'text'}
      className="lrc-input frm-input"
      data-testid={`frm-editor-${field.key}`}
      value={draft ?? ''}
      onChange={(e) => {
        setDraft(e.target.value);
        // F-59b: opt-in, and create-mode TOOL ID is the only caller. See the
        // commitOnChange note at the call site for why edit mode must not do
        // this. onBlur below stays as it was: after a per-keystroke commit
        // the value already matches, so commitValue's unchanged-value guard
        // makes the blur a true no-op rather than a second commit.
        if (commitOnChange) commitValue(e.target.value);
      }}
      onBlur={commit}
    />
  );
}

// Item 8: the id a bool's <label htmlFor> binds to. ONE definition for both
// ends of the pairing, so the label and its checkbox cannot drift apart.
// Unique per modal: one editor per key, and the host mounts one modal.
const boolInputId = (key) => `frm-bool-${key}`;

function BoolEditor({ field, record, onCommit }) {
  const checked = record.working?.[field.key] === true;
  return (
    <input
      type="checkbox"
      id={boolInputId(field.key)}
      className="frm-checkbox"
      data-testid={`frm-editor-${field.key}`}
      checked={checked}
      onChange={(e) => onCommit(field.key, e.target.checked)}
    />
  );
}

// 5.3 Phase 1 (D-5.3-8): presentation swap — the <select> became a trigger
// button that opens the modal-level BottomSheetSelect. Options source, refKey
// lookup, and commit-if-changed semantics are unchanged; they now live at the
// host's sheet handlers (the sheet is a SINGLE instance, opened per-field).
function EnumEditor({ field, record, onOpenSheet }) {
  const current = record.working?.[field.key] ?? '';
  return (
    <button
      type="button"
      className="lrc-input frm-input frm-enum-trigger"
      data-testid={`frm-editor-${field.key}`}
      onClick={() => onOpenSheet(field)}
      aria-haspopup="listbox"
    >
      <span className={`frm-enum-value${current ? '' : ' frm-enum-value-empty'}`}>
        {current || '—'}
      </span>
      <svg className="frm-enum-chevron" width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
        <path d="M4 6L8 10L12 6" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </button>
  );
}

/* ── Phase 3 child-set helpers ───────────────────────────────────────────── */

const mergedChildren = (record, group) => [
  ...(record[group] || []),
  ...(record[`extra_${group}`] || []),
];

const childKey = (entry) => entry.id ?? entry.local_key;

/** case-insensitive trim-compare dupe check (D-5.2-H) */
const hasDupeValue = (entries, value, excludeKey = null) => {
  const needle = value.trim().toLowerCase();
  return entries.some((e) =>
    childKey(e) !== excludeKey
    && typeof e.value === 'string'
    && e.value.trim().toLowerCase() === needle);
};

/* ── B5: what an edit-mode Save tells the rep ────────────────────────────────
   enqueueFieldCapture returns null for two reasons that the footer used to
   render identically as "No changes to send": an empty diff, and a pending-
   record it refuses on purpose (D-5.2b-E — no tool_id or etag yet; the dirt
   rides the re-key follow-up). The second was false: those edits exist and
   are sent when the record syncs. The refusal itself is unchanged; this only
   names the outcome. The pending test is the same predicate the outbox
   guard uses (isPendingRecord), so the two cannot disagree on which records
   are refused. Order matters: a capture that DID queue is 'queued' whatever
   else is true.

   2c: a pending record's edits only "send with the new tool" if a
   tool_create EXISTS for it. With none (the sheet's 'orphan', Bob's
   285/286-1-W) nothing is on its way, so the outcome is 'unsent' and the rep
   is pointed at the sheet's Send. `hasToolCreate` is the caller's queue read:
   true / false, or null when it could not be read — and null names NO
   outcome, because a guess here is the exact lie this phase removes. */
function saveOutcome(outboxId, record, photosQueued, hasToolCreate) {
  if (outboxId) return 'queued';
  const dirty = record?.dirty || {};
  if (isPendingRecord(record?.record_id) && Object.keys(dirty).some((k) => dirty[k])) {
    if (hasToolCreate === true) return 'held';
    if (hasToolCreate === false) return 'unsent';
    return null;
  }
  if (photosQueued > 0) return 'photos';
  return 'noop';
}

/* ── Create-mode draft (5.2b build-B-i, D-5.2b-C) ────────────────────────── */

// Pure in-memory row-shaped draft — NO fieldDb write until Save (build-B-ii
// mints pending-* + enqueues; §6 close-without-save discards for free).
// UNIT defaults to LBS (Bob's semantics), and FOUND and ADDED to checked (Bob
// P2-2 part C: the rep is standing at the tool). Each is marked dirty so the
// create flow flushes it; everything else starts at its natural empty state.
// buildDuplicateDraft (data/duplicateRecord.js) seeds the same three.
function mkDraft(site) {
  return {
    record_id: null,
    tool_id: null,
    site_id: site?.site_id ?? null,
    etag: null,
    baseline: {},
    working: { weight_unit: 'LBS', found: true, added_by_tmg: true },
    dirty: { weight_unit: true, found: true, added_by_tmg: true },
    part_numbers: [],
    extra_part_numbers: [],
    asset_tags: [],
    extra_asset_tags: [],
    media: [],
    cached_at: null,
  };
}

// TOOL ID. Create mode renders it first, committed through the draft sink.
// Since P2-5 (B-218) it is in the capture allowlist and edit mode renders it
// too, in its manifest position (fieldRow), committed through updateWorkingField.
const TOOL_ID_FIELD = { key: 'supplier_tool_id', label: 'TOOL ID *', kind: 'text' };

// Keys create mode never shows as editors. EMPTY since Bob P2-2 part C
// (2026-10-28): FOUND and ADDED were here (D-5.2b-F) and now render pre-checked,
// seeded by mkDraft. Kept, like DISPOSAL_ONLY_FIELDS, as the seam a future
// create-hidden key lands in.
const CREATE_HIDDEN_KEYS = new Set();

// Bob P1 item 2b: which of fieldsForView's entries are read-only context rows.
// MEMBERSHIP, not `!f.kind`. FieldColumnsModal tells the same rows apart by
// their missing `kind`; the two predicates are pinned to agree by
// editableFields.kindInvariant.test.js rather than by sharing one call site.
const READONLY_DISPLAY_KEYS = new Set(READONLY_DISPLAY_FIELDS.map((f) => f.key));

// Unit 4 Phase 3: stable empty default for the `hidden` prop, same reason as
// FieldRecordsList's EMPTY_DIRTY_IDS / EMPTY_HIDDEN — a `new Set()` written as
// a default parameter mints a fresh identity every render, and a missing prop
// must put every field in the main body rather than throw on .has().
const EMPTY_HIDDEN = new Set();

// Wave 2 item 5: stable empty default for the `records` prop, same reason as
// EMPTY_HIDDEN. Module scope, never mutated.
const EMPTY_RECORDS = [];

// Wave 2 item 5 (Bob 09-22): the seven location fields edit through
// FieldComboSheet — a searchable picker over the values this site already
// uses, plus create-new — instead of TextEditor, so reps stop typing their
// own spellings. Their manifest kind stays 'text' (editableFields.js); only
// the EDITOR changes, and only in editorFor.
const LOCATION_COMBO_KEYS = new Set([
  'site_location', 'detail_location',
  'row_nbr', 'stack_nbr', 'rack_nbr', 'shelf_nbr', 'skid_nbr',
]);

// The enum sheets ranked most-used-first (P2-3). MATERIAL and UNIT share the
// sheet and keep their refdata order.
const RANKED_ENUM_KEYS = new Set(['tool_type', 'oem']);

/* ── Modal ───────────────────────────────────────────────────────────────── */

export default function FieldRecordModal({
  record, site, callerEmail, isOpen, onClose, onRecordChange, mode = 'edit',
  fieldView = 'inv', hidden = EMPTY_HIDDEN,
  onRecordRemoved, syncing = false,
  // Wave 2 item 5: the host's RAW on-device records for the site — the same
  // array FieldFilterModal's value picker reads — so the location picker can
  // offer the values this site already uses.
  records = EMPTY_RECORDS,
  // P2-5 Phase 3 (B-198): the record menu's Duplicate. onDuplicate(record) asks
  // the host to open create mode seeded from that record; createSeed is the
  // seed it hands back (data/duplicateRecord.buildDuplicateDraft), or null for
  // an ordinary Add. No onDuplicate, no menu.
  onDuplicate = null,
  createSeed = null,
}) {
  const isCreate = mode === 'create';
  // INV is the default (the prop default, not a local state seed) so the
  // host owns the value and it survives record-to-record within a session.
  const isAllView = fieldView === 'all';
  const { options } = useFieldReferenceData();

  // ── BottomSheetSelect sentinel (5.3 Phase 1, D-5.3-8) ──────────────────
  // Mirrors the modal's own tmgFieldModalCreate mechanism (D-5.2-A): open is
  // a SAME-URL history push whose state SPREADS the current entry's state
  // (the modal's record/create sentinel must survive onto the pushed entry —
  // back closes the SHEET only, the modal stays open underneath) plus
  // tmgFieldSheetField. isOpen derives from location.state; EVERY close path
  // (row-select, backdrop, drag-down, Escape, hardware/gesture back) resolves
  // through the same navigate(-1) pop.
  const location = useLocation();
  const navigate = useNavigate();
  const sheetFieldKey = location.state?.tmgFieldSheetField ?? null;

  const openSheet = useCallback((field) => {
    navigate(location.pathname + location.search, {
      state: { ...(location.state || {}), tmgFieldSheetField: field.key },
    });
  }, [navigate, location.pathname, location.search, location.state]);

  const closeSheet = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  // P2-3: TOOL TYPE and OEM open most-used-first, counted from this site's
  // records; every other enum sheet keeps the refdata order. Ranked HERE, not
  // in BottomSheetSelect, so the counting rule lives in one place and the sheet
  // never sees records. Above the hook barrier, so it keys on the raw
  // sentinel; for these two keys the refKey is the key. Built once per open:
  // the refdata `options` are a dependency because they land asynchronously,
  // and nothing on this sheet is typed, so no keystroke reaches it.
  const rankedEnumOptions = useMemo(() => {
    if (!isOpen || !RANKED_ENUM_KEYS.has(sheetFieldKey)) return null;
    return rankByUsage(options[sheetFieldKey] || [], records, sheetFieldKey);
  }, [records, sheetFieldKey, isOpen, options]);

  // ── FieldComboSheet sentinel (Wave 2 item 5) — the location picker's OWN
  // key, same push/spread/pop mechanics as tmgFieldSheetField above: open is
  // a same-URL push that SPREADS the current state (the record's sentinel
  // rides onto the pushed entry), so a hardware/gesture back pops the SHEET
  // and the record stays open underneath. Every close path is navigate(-1).
  const comboFieldKey = location.state?.tmgFieldComboField ?? null;

  const openCombo = useCallback((field) => {
    navigate(location.pathname + location.search, {
      state: { ...(location.state || {}), tmgFieldComboField: field.key },
    });
  }, [navigate, location.pathname, location.search, location.state]);

  const closeCombo = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  // ── FieldMediaViewer sentinel (5.3 Phase 2) — the viewer's OWN key,
  // same push/spread/pop mechanics as the sheet's tmgFieldSheetField.
  // The index seeds the viewer; swiping is viewer-local state (never nav).
  const viewerIdx = location.state?.tmgFieldMediaViewerIdx ?? null;

  const openViewer = useCallback((index) => {
    navigate(location.pathname + location.search, {
      state: { ...(location.state || {}), tmgFieldMediaViewerIdx: index },
    });
  }, [navigate, location.pathname, location.search, location.state]);

  const closeViewer = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  // ── Option-1 liveness nudge (5.3 follow-up): on modal OPEN for a PERSISTED
  // row, re-read it from IDB once and push through the host's upsert — reopen
  // shows reconcile truth (e.g. stamped media, cleared pending) without a
  // site re-entry. Open-transition only — this feeds the HOST records array
  // (what the GRID renders from); the open MODAL's render reads live store
  // truth via the useLiveQuery block below (5.4 Cluster B) — two consumers.
  //   GUARD 1: create drafts (record_id null) never fire — a re-read would
  //            clobber the in-memory draft. pending-* rows ARE persisted: fine.
  //   GUARD 2: fires exactly once per open/record — deps are [isOpen, the
  //            record_id string]; onRecordChange rides a ref so identity
  //            churn can neither re-fire nor loop (the upsert changes the
  //            prop OBJECT, not the id). Never wired to interactions.
  //   GUARD 3: absent row (race/purge) = no-op — never push undefined.
  const onRecordChangeRef = useRef(onRecordChange);
  onRecordChangeRef.current = onRecordChange;

  // F-44: the tail of the most recent scalar commit. TextEditor's `commit`
  // is a SYNC function that calls onCommit without awaiting it, and React's
  // onBlur cannot await a handler either — so a blur-commit's Dexie write
  // is genuinely fire-and-forget and can still be in flight when Save runs.
  // Save awaits this before reading, which is the only handle anything has
  // on that promise. Seeded resolved so the first Save never waits on
  // nothing. Holds the CAUGHT tail: a rejected commit must not turn Save
  // into an unhandled rejection — handleCommit already logs its own error.
  const pendingCommitRef = useRef(Promise.resolve());

  const persistedRecordId = !isCreate ? (record?.record_id ?? null) : null;
  useEffect(() => {
    if (!isOpen || !persistedRecordId) return undefined;
    let alive = true;
    getRecord(persistedRecordId)
      .then((row) => { if (alive && row) onRecordChangeRef.current?.(row); })
      .catch((err) => console.error('[fieldModal] open re-read failed:', err));
    return () => { alive = false; };
  }, [isOpen, persistedRecordId]);

  // ── Modal liveness (5.4 Cluster B): the OPEN modal renders the LIVE store
  // row, so mid-open Dexie writes (reconcile media-promote stamp, commit
  // sinks, gallery appends) reach the render without a close/reopen — this
  // also retires the stale-prop media PENDING-badge mis-render. ZONE
  // CONTRACT (useFieldQueue donor): the Dexie read must START synchronously
  // inside the tracked zone — never getRecord(), whose `await ensureOpen()`
  // hop kills dependency registration (the 5.4 foreign-await bug); the
  // closed-DB edge chains through fieldDb.open()'s DexiePromise. No error
  // handling in the querier by design (a rejected read surfaces through the
  // hook; a wrapper would re-introduce the zone-breaking hop). The guard
  // returns undefined SYNCHRONOUSLY (no read, no registered deps) for
  // create mode / no id; undefined ALSO covers not-yet-resolved and
  // row-absent (records.get of a missing key, e.g. a pending-* temp row
  // deleted by the create re-key mid-open) — every undefined arm falls back
  // to the prop snapshot where activeRecord derives below.
  //
  // RESOLVED-ABSENT IS null, NOT undefined (re-key fix): both arms append
  // `.then((r) => r ?? null)` so `undefined` means ONLY "not resolved yet"
  // and `null` means "resolved, and the row is not there". The Dexie read
  // still STARTS synchronously — the .then chains on its DexiePromise, so
  // dependency tracking is unchanged. A persisted id whose row resolves null
  // was re-keyed (reconcile deletes the pending- row and puts the real one,
  // reconcileConfirmations D.1) or deleted underneath the open modal.
  const liveRow = useLiveQuery(
    () => {
      if (!persistedRecordId) return undefined;
      return fieldDb.isOpen()
        ? fieldDb.records.get(persistedRecordId).then((r) => r ?? null)
        : fieldDb.open()
          .then(() => fieldDb.records.get(persistedRecordId))
          .then((r) => r ?? null);
    },
    [persistedRecordId],
  );

  // PN/AT add-row state (host-owned per the lists' contract) + inline errors
  const [newPn, setNewPn] = useState('');
  const [newPnType, setNewPnType] = useState('TBD');
  const [newPnDesc, setNewPnDesc] = useState('');
  const [newTag, setNewTag] = useState('');
  const [pnError, setPnError] = useState(null);
  const [atError, setAtError] = useState(null);
  const [saveState, setSaveState] = useState(null); // null | 'queued' | 'held' | 'photos' | 'noop' | 'error' | 'blocked'
  // B5 (Bob's P2-4): photos the edit-mode gallery queued during THIS open.
  // A photo has no dirty sentinel, so a photo-only Save builds no capture and
  // used to report "No changes to send" over a photo that did save. Counted
  // at the gallery's onRecordChange, which fires once per queued photo and
  // only on success; reset with the rest of the per-record state below.
  // savedPhotoCount is the count AS OF the Save that reported it, so the note
  // cannot drift if another photo lands afterwards.
  // 2c-4: a SET of the queued photos' outbox_ids, not a counter, so a photo
  // deleted again in the same open (handleMediaDelete) stops counting — a
  // counter said "Photo saved ✓" over a photo the rep had just removed.
  const photosQueuedRef = useRef(new Set());
  const [savedPhotoCount, setSavedPhotoCount] = useState(0);
  // A scalar commit that FAILED to reach the store: the label of the field,
  // or null. updateWorkingField throws inside its transaction and the old
  // handler only logged — the editor kept showing a value that was never
  // saved. Cleared by the next successful commit.
  const [commitError, setCommitError] = useState(null);
  // J2a: reported up by the two child lists. They own the typed value; the
  // modal only needs to know an edit row is OPEN.
  const [pnEditOpen, setPnEditOpen] = useState(false);
  const [atEditOpen, setAtEditOpen] = useState(false);
  // P2-5 Phase 2 (B-218): TOOL ID edits in edit mode.
  //   toolIdNote      — the footer note for TOOL ID: {kind: 'dupe'|'blank', text}, or null.
  //   toolIdConfirmRef — the match key the rep was already warned about; a Save
  //                      with that same TOOL ID goes through (non-blocking).
  //   toolIdAtOpenRef — the TOOL ID when this record was opened. A pending row
  //                      has no server baseline, so "did the rep change it?" is
  //                      asked against this instead.
  //   toolIdEditorKey — bumped to remount the TOOL ID editor after a refused
  //                      blank, so it shows the stored value again.
  const [toolIdNote, setToolIdNote] = useState(null);
  const toolIdConfirmRef = useRef(null);
  const toolIdAtOpenRef = useRef(null);
  const [toolIdEditorKey, setToolIdEditorKey] = useState(0);
  // P2-5 Phase 3: the record menu (edit mode) and whether this create session
  // is a duplicate (its TOOL ID editor focuses and selects on mount).
  const [menuOpen, setMenuOpen] = useState(false);
  const [duplicateSession, setDuplicateSession] = useState(false);

  // In-memory create draft. Reset on every create-mode open so a discarded
  // draft never leaks into the next Add. createSession keys the modal content
  // so the editor subtree REMOUNTS per Add — the editors' local input state
  // re-seeds only on record_id change, which is null→null across create
  // sessions; without the key, stale text survives a draft reset. The key is
  // stable within a session, so typing never remounts (focus survives).
  const [draft, setDraft] = useState(() => mkDraft(site));
  // B1 (P2-1): the draft's SYNCHRONOUS mirror — mediaBufferRef's convention,
  // a ref beside the state it shadows for handlers that cannot wait on a
  // re-render. One difference, and it is the point: this one is written
  // AHEAD of render, by the draft's three writers (the session reset and the
  // two draft sinks), never re-copied from state during render. handleCreate
  // reads ONLY this. A blur-commit's setDraft is invisible to a handler
  // already running from an earlier render's closure — which is how a typed,
  // never-blurred ESTIMATED WEIGHT missed the pending row on 44 of 81 field
  // creates. setDraft stays, for rendering.
  const draftRef = useRef(draft);
  const [createSession, setCreateSession] = useState(0);
  const [createError, setCreateError] = useState(null);
  const [dupeConfirmId, setDupeConfirmId] = useState(null);
  // 5.3 Phase 3a (D-5.3-P3-1): create-mode media buffer — parallel to the
  // scalar draft, NOT part of it. Entries {buffer_id, pending:true,
  // object_url, blob_data, file_ext, photo_type} render through the Phase 2
  // gallery/viewer as optimistic thumbs; NOTHING is enqueued until Create
  // flushes them (no record_id exists to enqueue against). The ref mirror
  // lets the session reset below read the current buffer without adding it
  // to the reset's deps (which would wipe the buffer mid-session).
  const [mediaBuffer, setMediaBuffer] = useState([]);
  const mediaBufferRef = useRef([]);
  mediaBufferRef.current = mediaBuffer;
  // D-2 in-flight create marker: null, or { pendingId } from the moment the
  // temp row is DURABLE. A ref, not state — handleCreate must read what its
  // OWN previous invocation wrote, without waiting on a re-render. Same
  // lifecycle owner as mediaBufferRef (the create-session reset below) plus
  // one clear on full success; no third reset path.
  const inFlightCreateRef = useRef(null);

  // F-58b: Save's re-entry guard, and inFlightCreateRef's counterpart. A ref
  // rather than state for the same reason: handleSave must see what its own
  // previous invocation wrote without waiting for a re-render. The pointer
  // handlers on the Save button mean one activation can deliver BOTH a
  // pointerup and a click; this is what makes the second one free.
  const saveInFlightRef = useRef(false);

  // F-59a: the same guard for Create. NOT inFlightCreateRef, which is the
  // D-2 resume marker and is only set once the temp row is DURABLE — two
  // invocations racing BEFORE that point would both mint a pendingId and turn
  // one draft into two tools, which is exactly what the marker cannot catch.
  const createInFlightRef = useRef(false);

  // ── Field cancel Phase 2: Discard (spec v3 §4-§5, amended 2B) ───────────
  // The dialog is LOCAL state, not a sentinel — FieldMediaViewer's confirm
  // pattern: hardware back with it open closes the modal, one sentinel per
  // surface. Shapes:
  //   null                              — closed
  //   { kind: 'confirm', photoCount }   — the §5 confirm
  //   { kind: 'blocked' }               — preflight/primitive said
  //                                       'already_created': no confirm copy
  //                                       claiming "never sent" is ever shown
  // discardError is an honest post-confirm refusal or failure; discardFutile
  // collapses the confirm's footer to one Close when a retry cannot work.
  // discardNote is a preflight read failure, shown in the footer note slot.
  const [discardDialog, setDiscardDialog] = useState(null);
  const [discardError, setDiscardError] = useState(null);
  const [discardFutile, setDiscardFutile] = useState(false);
  const [discarding, setDiscarding] = useState(false);
  const [discardNote, setDiscardNote] = useState(null);
  // Same double-delivery guard as Save/Create: the pointer-capture handlers
  // mean one activation can deliver both a pointerup and a click.
  const discardTapInFlightRef = useRef(false);
  // True from the moment OUR Discard removed the row until the modal closes.
  // Without it, the live query resolving null in the frame before the host's
  // pop lands would render the "just synced" notice over a record the rep
  // just discarded — the opposite of what happened.
  const [discardSucceeded, setDiscardSucceeded] = useState(false);
  // (Cleared on every open and close by THE per-open reset, below.)

  // Row gone underneath the modal (re-key): an open Discard dialog is now
  // describing a record that no longer exists under this id — its "not
  // created on the server" copy would be false. Close it; the re-keyed
  // notice takes over.
  useEffect(() => {
    if (liveRow === null) setDiscardDialog(null);
  }, [liveRow]);

  // Escape while the dialog is up: CAPTURE phase + stopPropagation, the
  // FieldMediaViewer precedent. ModalShell listens in the BUBBLE phase and
  // closes the whole modal — without capture, Escape here would close the
  // record too. Mid-commit, Escape does nothing (the outcome is not known).
  useEffect(() => {
    if (!discardDialog) return undefined;
    const onKeyDown = (e) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      if (!discarding) setDiscardDialog(null);
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [discardDialog, discarding]);

  // ── Unit 4 Phase 3: Hidden Fields section collapse ──────────────────────
  // Local, ephemeral, and deliberately NOT on the router: this is a disclosure
  // state, not a navigable layer, so it must not consume a history entry that
  // Android back would spend on it instead of on closing the modal. Also not
  // persisted — unlike the hidden SET itself, which useFieldHiddenFields keeps
  // in localStorage, whether the drawer happens to be open is not a
  // preference worth remembering.
  //
  // Declared HERE rather than beside the split it feeds, because the modal
  // early-returns at `if (!activeRecord) return null` and no hook may sit
  // below that line.
  const [hiddenOpen, setHiddenOpen] = useState(false);
  // (Default COLLAPSED on every open — THE per-open reset, below.)

  // The gallery hands buffered captures here in create mode (stable
  // identity — hook must live above the modal's early return).
  const handleBufferCapture = useCallback((photo) => {
    setMediaBuffer((prev) => [
      ...prev,
      { buffer_id: crypto.randomUUID(), pending: true, ...photo },
    ]);
  }, []);
  // useLayoutEffect: reset BEFORE paint, or reopen flashes the stale draft
  // for a frame (useEffect fires after paint).
  useLayoutEffect(() => {
    if (isCreate && isOpen) {
      // P2-5 Phase 3: a duplicate starts from the host's seed — a copy, so the
      // session never shares objects with the seed (or with a later one).
      const freshDraft = createSeed ? JSON.parse(JSON.stringify(createSeed)) : mkDraft(site);
      setDuplicateSession(Boolean(createSeed));
      draftRef.current = freshDraft; // B1: the ref rides the SAME reset
      setDraft(freshDraft);
      // Discard rides the SAME reset (build-B-i): the PRIOR create session's
      // buffered previews are revoked here, so a new Add starts empty and
      // nothing leaks across Adds. A FLUSHED buffer is already empty —
      // ownership of those object_urls moved to the temp row at Create.
      for (const m of mediaBufferRef.current) {
        if (m.object_url) {
          try { URL.revokeObjectURL(m.object_url); } catch (revokeErr) { /* non-fatal */ }
        }
      }
      setMediaBuffer([]);
      inFlightCreateRef.current = null; // D-2: marker rides the SAME reset
      setCreateSession((n) => n + 1);
      setCreateError(null);
      setDupeConfirmId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCreate, isOpen]);

  // Draft-local commit sink — IDENTICAL signatures to the store handlers,
  // but a setState on the draft. STABLE identities (useCallback): a new
  // function identity per render is fine for handlers, but these also feed
  // effect-free editors — keep them stable regardless (remount-trap hygiene;
  // the editor COMPONENTS are module-scope, never render-scoped).
  //
  // B1: both sinks build `next` from draftRef — NOT a setDraft updater's
  // `prev`, which only runs at the next render — write the ref, THEN hand the
  // same object to setDraft. Rapid commits still compose: each reads the ref
  // the previous one wrote. After a sink returns, the ref already holds the
  // commit, so a blur fired synchronously by handleCreate is visible to it on
  // the very next line.
  const draftCommitScalar = useCallback((key, value) => {
    const prev = draftRef.current;
    const next = {
      ...prev,
      working: { ...prev.working, [key]: value },
      dirty: { ...prev.dirty, [key]: true },
    };
    draftRef.current = next;
    setDraft(next);
  }, []);

  const draftCommitChildren = useCallback((group, entries) => {
    const prev = draftRef.current;
    const next = {
      ...prev,
      [group]: entries,
      [`extra_${group}`]: [],
      dirty: { ...prev.dirty, [group]: true },
    };
    draftRef.current = next;
    setDraft(next);
  }, []);

  // ── Phase 3: THE per-open reset ──────────────────────────────────────────
  // The host keeps this component MOUNTED while it is closed (ModalShell
  // returns null), so per-record UI state survives a close unless it is
  // cleared here. ONE effect for the whole class, keyed on the identity
  // triple the editor subtree's remount key uses — isOpen, the persisted
  // record id (edit), the create session (create) — so "a new record" means
  // one thing everywhere. It runs on every close and every open.
  //
  // It replaces three effects with three different keys, none of which held
  // the child-list flags: an edit row open at close left childEditPending
  // true, so the NEXT Save and the NEXT Create were refused over a part
  // number the rep could no longer see.
  //
  // Deliberately NOT here:
  //   - the create draft, draftRef, the media buffer, the D-2 marker and
  //     createSession itself: the create-session layout effect owns them,
  //     because a new create identity must exist before paint;
  //   - pendingCommitRef: a settled promise is harmless to await, but
  //     replacing it could drop Save's wait on an unmount-commit write still
  //     in flight for the same record (the F-44 hazard);
  //   - saveInFlightRef / createInFlightRef / discardTapInFlightRef and
  //     `discarding`: each is released by its own finally, and clearing one
  //     mid-flight would re-open the double-tap it guards;
  //   - liveRow: the live query is re-derived per record id.
  useEffect(() => {
    setPnEditOpen(false); setAtEditOpen(false); // the child lists' report
    setNewPn(''); setNewPnType('TBD'); setNewPnDesc('');
    setNewTag(''); setPnError(null); setAtError(null);
    setSaveState(null); setCommitError(null);
    photosQueuedRef.current = new Set(); setSavedPhotoCount(0); // B5: per open, never carried
    setDiscardDialog(null); setDiscardError(null); setDiscardFutile(false);
    setDiscardNote(null); setDiscardSucceeded(false);
    setHiddenOpen(false); // the drawer defaults COLLAPSED on every open
    setToolIdNote(null); toolIdConfirmRef.current = null; // P2-5: warn afresh per open
    setMenuOpen(false); // P2-5 Phase 3: the record menu never survives a switch
    toolIdAtOpenRef.current = record?.working?.supplier_tool_id ?? null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, persistedRecordId, createSession]);

  // F-51: publish the visual viewport's height/offset as custom properties on
  // documentElement, which the <1024px .frm-dialog block consumes. iOS does
  // not shrink the LAYOUT viewport for the keyboard, so ModalShell's 85vh
  // dialog keeps full height and its footer lands under the keyboard.
  //
  // BOTH listeners are load-bearing: resize covers the keyboard opening and
  // closing, scroll covers offsetTop, which only changes when Safari
  // translates the fixed layer to reveal a focused input.
  //
  // Cleanup MUST removeProperty both — these live on documentElement, so
  // leaving them set would leak this modal's geometry onto every other
  // surface after it closes.
  //
  // The !vv guard keeps the CSS fallbacks (0px / 100dvh) reachable on
  // browsers without visualViewport; setting nothing is the fallback path.
  useEffect(() => {
    if (!isOpen) return undefined;
    const vv = window.visualViewport;
    if (!vv) return undefined;
    const root = document.documentElement;
    const apply = () => {
      root.style.setProperty('--frm-vv-height', `${vv.height}px`);
      root.style.setProperty('--frm-vv-top', `${vv.offsetTop}px`);
    };
    apply();
    vv.addEventListener('resize', apply);
    vv.addEventListener('scroll', apply);
    return () => {
      vv.removeEventListener('resize', apply);
      vv.removeEventListener('scroll', apply);
      root.style.removeProperty('--frm-vv-height');
      root.style.removeProperty('--frm-vv-top');
    };
  }, [isOpen]);

  // Prop = first-paint + create-mode source; the live row takes over once
  // resolved. undefined (unresolved) AND null (resolved absent) both fall
  // back to the prop snapshot, so a mid-open row delete freezes on the last
  // prop rather than blanking. `liveRow != null`, never `!== undefined`:
  // with null possible, `!== undefined` would hand null to activeRecord and
  // the early return below would make the modal vanish mid-typing.
  const effectiveRecord = (!isCreate && liveRow != null) ? liveRow : record;
  const activeRecord = isCreate ? draft : effectiveRecord;
  if (!activeRecord) return null;

  // ── Re-keyed underneath the open modal ──────────────────────────────────
  // The store resolved this record's id to NO row: reconcile re-keyed it
  // (pending- → server id, delete + put) while the modal was open. The modal
  // cannot follow it — reconcile writes no link from the old id to the new
  // one — so it freezes on the prop snapshot (effectiveRecord above) and
  // STOPS ACCEPTING INPUT: every write here would target the deleted id and
  // throw. It does NOT close itself; the rep needs to read why their input
  // stopped landing. Our own successful Discard is excluded (see
  // discardSucceeded).
  const recordGone = !isCreate && liveRow === null && !discardSucceeded;

  // Edit mode delegates to the shared helper (F-53): its fourth term,
  // record_id, is what stops a record with no TOOL ID and no description
  // from rendering a blank title. Create mode keeps its own branch — 'New
  // Tool' is a literal for a draft that has no identity yet, not a fallback.
  const title = isCreate
    ? (activeRecord.working?.supplier_tool_id || 'New Tool')
    : recordLabel(activeRecord);

  // NO DISPOSAL GATE ANY MORE (Unit F, 2026-09-04). `marked` was the only
  // member of DISPOSAL_ONLY_FIELDS and moved into BASE_FIELDS, so it is now
  // offered on EVERY site rather than only DISPOSAL ones. The old branch —
  // `isDisposalSite ? [...EDITABLE_FIELDS, ...DISPOSAL_ONLY_FIELDS] :
  // EDITABLE_FIELDS` — became inert the moment that array emptied, and an
  // inert branch reading a permanently-empty import is worse than no branch:
  // it tells the next reader a gate is doing work when nothing is.
  //
  // Restore the branch if DISPOSAL_ONLY_FIELDS is ever re-populated; its
  // docblock in editableFields.js says the same thing from the other side.
  // fieldsForView takes no site, so a restored gate appends to `viewFields`
  // below rather than to the shared mapping.
  //
  // VIEW FILTER (INV/ALL) — fieldsForView, the ONE view→field-set mapping
  // the records grid, the search overlay and the Columns / Sort / Filter /
  // Group pickers already read (Bob P1 item 2b). This form used to keep its
  // own copy; two copies that agree today is how the grid once ignored the
  // view entirely.
  //
  // ONE SEQUENCE (Bob P1 item 2b). fieldsForView returns the view's fields in
  // FIELD_ORDER, read-only rows interleaved with the editors, and edit mode
  // renders that list as it comes: fieldRow picks the renderer per field, so
  // a read-only row sits in its manifest position exactly as its grid column
  // does. Nothing here sorts; the only thing spliced in is MEDIA.
  //
  // Composed ALONGSIDE the create filter rather than folded into it: two
  // independent reasons to hide a field, each readable on its own. Order is
  // irrelevant — both are pure predicates over the same list.
  //
  // PRESENTATION ONLY. This narrows what is RENDERED, never what is stored
  // or sent: commits write through to activeRecord.working (Dexie in edit
  // mode, the draft object in create), and buildCapturePayload iterates
  // `dirty` sentinels rather than any field list. A field edited in ALL and
  // then hidden by a switch to INV still ships on Save.
  //
  // The scrap trio (approved_scrap, marked, scrap_project_number) needs no
  // clause here: none of the three is in the INV set, so the view filter
  // alone makes them ALL-view only, on every site.
  const viewFieldList = fieldsForView(fieldView);
  const viewFields = viewFieldList.filter((f) => !READONLY_DISPLAY_KEYS.has(f.key));
  // Create mode drops CREATE_HIDDEN_KEYS (empty since P2-2 part C; see its note).
  const editableFields = isCreate
    ? viewFields.filter((f) => !CREATE_HIDDEN_KEYS.has(f.key))
    : viewFields;

  // The rows the form renders, as ONE list. Edit: the whole view list, read-
  // only rows (those READONLY_INV_KEYS admits in INV, every one in ALL) in
  // place among the editors. Create: the editors alone — no read-only rows
  // exist before the tool does.
  const formFields = isCreate ? editableFields : viewFieldList;

  // MEDIA's slot, after MEDIA_AFTER_KEY as in the grid, and taken over the
  // list BEFORE the hide split below, as the grid's is (columnsForView
  // splices, visibleColumnsFor then hides): hiding ADDED leaves MEDIA where
  // ADDED was. A missing anchor appends, as there. Create mode pins MEDIA
  // first, under the TOOL ID editor: a rep standing at a tool with a camera
  // wants it high.
  const mediaAnchor = formFields.findIndex((f) => f.key === MEDIA_AFTER_KEY);
  const mediaAt = isCreate ? 0 : (mediaAnchor === -1 ? formFields.length : mediaAnchor + 1);

  // ── Unit 4 Phase 3: the HIDE split ──────────────────────────────────────
  // Runs LAST, over the output of the view and create filters above, and is
  // deliberately NOT folded into them. Three independent reasons a field may
  // not be in the main body, each readable on its own line — and only this
  // one RELOCATES rather than removes. A field the view filter dropped is
  // gone; a field this split moves is still on screen, still editable, still
  // committing through the same sink, one tap away in the drawer below.
  //
  // PRESENTATION ONLY, exactly as the view filter above is. This narrows
  // what each band RENDERS and touches nothing about what is stored or sent:
  // buildCapturePayload iterates the record's `dirty` sentinels, never a
  // field list, so a value typed in the drawer and then collapsed out of
  // sight still ships on Save. TextEditor's unmount commit (commitRef) is what
  // makes the collapse itself safe — tearing down a focused editor flushes
  // its draft through the same onCommit, and an unchanged value is a true
  // no-op there, so collapsing a clean field cannot dirty it.
  const visibleBeforeMedia = formFields.slice(0, mediaAt).filter((f) => !hidden.has(f.key));
  const visibleAfterMedia = formFields.slice(mediaAt).filter((f) => !hidden.has(f.key));
  const hiddenFields = formFields.filter((f) => hidden.has(f.key));

  // Counted over the list the drawer renders, so a header promising N rows
  // opens onto N rows — create mode's has no read-only rows to count.
  const hiddenCount = hiddenFields.length;

  const materialSource = activeRecord.working?.material_source ?? activeRecord.baseline?.material_source ?? null;

  // Mode-switched sinks: edit → store point-read/write; create → draft setState.
  const handleCommit = async (key, value) => {
    if (isCreate) {
      draftCommitScalar(key, value);
      return;
    }
    // Re-keyed: the id is gone, so the write can only throw. The editors are
    // disabled in this state; this catches a commit already in flight — an
    // open enum sheet's row tap, or a focused TextEditor's unmount flush.
    // The re-keyed notice explains it; no second message.
    if (recordGone) return;
    if (key === TOOL_ID_FIELD.key) {
      // P2-5: TOOL ID is required, as at Create. A blank is refused HERE, not at
      // Save: a pending row's edit reaches its queued create through
      // updateWorkingField, and the follow-up capture after the create never
      // passes through Save. The editor remounts on the stored value.
      if (value === null || value === undefined || String(value).trim() === '') {
        setToolIdNote({ kind: 'blank', text: "TOOL ID can't be blank — it was not changed" });
        setToolIdEditorKey((n) => n + 1);
        return;
      }
      setToolIdNote(null); // a warning about the previous value no longer applies
    }
    try {
      const updated = await updateWorkingField(activeRecord.record_id, key, value);
      if (updated && onRecordChange) onRecordChange(updated);
      setSaveState(null);
      setCommitError(null);
    } catch (err) {
      console.error(`[fieldModal] commit failed for ${key}:`, err);
      // B4 (re-key fix): a failed write must not wear the face of a saved one.
      // The field's LABEL, so the rep knows which value to re-enter.
      const field = [...EDITABLE_FIELDS, TOOL_ID_FIELD].find((f) => f.key === key);
      setCommitError(field?.label || key);
    }
  };

  // F-44: the tracking wrapper. handleCommit's body is UNCHANGED; this only
  // records the in-flight promise so Save can await it. Everything that
  // commits a scalar goes through here — the three editor wirings and the
  // enum sheet's row tap — so no commit path is left untracked.
  const trackedCommit = (key, value) => {
    const p = handleCommit(key, value);
    pendingCommitRef.current = p.catch(() => {});
    return p;
  };

  /* ── Media delete (5.3 Phase 2) — viewer confirm lands here. Pending-first
     per D-4.AG: cancelPendingMediaAdd is a pure local dequeue; false means
     the photo reached the server (or was never pending) → resolve a media_id
     (entry stamp, else the drained item's saved marks) → enqueueMediaRemove.
     The local row entry is removed either way (media has no dirty sentinel —
     re-Load owns media truth, so an un-enqueued orphan resurfaces there
     rather than being silently lost). ── */
  // B5: the edit-mode gallery's ONE callback, counted on the way through to
  // the host. FieldMediaGallery calls it once per photo, after the photo's
  // enqueue and row write have both succeeded, and never for anything else —
  // so the count is exactly "photos queued in this open". The host upsert is
  // forwarded unchanged. The new photo is the LAST media entry — the gallery
  // appends it — so its outbox_id is what handleMediaDelete can later remove.
  // A missing id still counts (a unique token), never silently drops.
  const handleGalleryRecordChange = (row) => {
    const media = row?.media || [];
    photosQueuedRef.current.add(media[media.length - 1]?.outbox_id ?? Symbol('photo'));
    onRecordChange?.(row);
  };

  const handleMediaDelete = async (entry) => {
    if (!entry) return;
    // Re-keyed: the photo's queue item was re-keyed to the new id, and
    // cancelPendingMediaAdd looks it up by outbox_id alone — it would DEQUEUE
    // a photo that now belongs to the real record, and the row write below
    // would find no row. An already-open viewer is the only way here.
    if (recordGone) return;
    // Create mode (Phase 3a): buffered photo — nothing was enqueued, so
    // delete = revoke the preview + drop from the buffer. No cancel path
    // exists or is needed.
    if (isCreate) {
      if (!entry.buffer_id) return;
      if (entry.object_url) {
        try { URL.revokeObjectURL(entry.object_url); } catch (revokeErr) { /* non-fatal */ }
      }
      const next = mediaBuffer.filter((m) => m.buffer_id !== entry.buffer_id);
      setMediaBuffer(next);
      if (next.length === 0) closeViewer(); // last buffered photo gone
      return;
    }
    try {
      let handled = false;
      if (entry.pending && entry.outbox_id) {
        handled = await cancelPendingMediaAdd(entry.outbox_id);
      }
      if (!handled) {
        let mediaId = entry.media_id ?? null;
        if (!mediaId && entry.outbox_id) {
          // drained-but-unreconciled: the saved marks on the queue item carry it
          const items = await getQueue();
          mediaId = items.find((i) => i.outbox_id === entry.outbox_id)?.media_id ?? null;
        }
        if (mediaId) {
          await enqueueMediaRemove({
            record_id: activeRecord.record_id,
            tool_id: activeRecord.tool_id,
            site_id: activeRecord.site_id,
            media_id: mediaId,
          });
        }
        // No id resolvable (2xx parse_failed orphan): local-only removal;
        // the next re-Load resurfaces the server doc for a retry.
      }
      // 2c-4: a photo queued in this open and now removed is no longer one a
      // photo-only Save can report as saved.
      if (entry.outbox_id) photosQueuedRef.current.delete(entry.outbox_id);
      await ensureOpen();
      const row = await fieldDb.records.get(activeRecord.record_id);
      if (row) {
        const key = entry.outbox_id ?? entry.media_id;
        row.media = (row.media || []).filter((m) => (m.outbox_id ?? m.media_id) !== key);
        await fieldDb.records.put(row);
        if (entry.object_url) {
          try { URL.revokeObjectURL(entry.object_url); } catch (revokeErr) { /* non-fatal */ }
        }
        onRecordChange?.(row);
        if (row.media.length === 0) closeViewer(); // last photo gone → viewer closes
      }
    } catch (err) {
      console.error('[fieldModal] media delete failed:', err);
    }
  };

  // Sheet target: resolved against the CURRENT mode's editable set (a stale
  // sentinel for a field not in this mode resolves null → sheet closed).
  const sheetField = sheetFieldKey
    ? editableFields.find((f) => f.kind === 'enum' && f.key === sheetFieldKey) || null
    : null;
  const sheetCurrent = sheetField ? (activeRecord.working?.[sheetField.key] ?? '') : '';

  // Row tap: commit-if-changed (verbatim <select> semantics — '' ⇄ null),
  // then close through the one sentinel pop.
  const handleSheetSelect = (next) => {
    if (sheetField && (sheetCurrent || null) !== next) {
      trackedCommit(sheetField.key, next);
    }
    closeSheet();
  };

  // Location picker target — resolved the same way as sheetField: against
  // this mode's editable set, so a stale sentinel resolves null (closed).
  const comboField = comboFieldKey
    ? editableFields.find((f) => LOCATION_COMBO_KEYS.has(f.key) && f.key === comboFieldKey) || null
    : null;
  const comboCurrent = comboField ? (activeRecord.working?.[comboField.key] ?? '') : '';

  // Row tap: commit-if-changed through trackedCommit (so Save still awaits
  // it, F-44), then close through the one sentinel pop — handleSheetSelect's
  // shape. `next` is a string, or null for "— none —". Compared as strings:
  // a loaded row may carry row_nbr as the NUMBER 3, which the sheet hands
  // back as "3" — the same location, not an edit.
  const handleComboSelect = (next) => {
    if (comboField) {
      const before = comboCurrent === '' ? null : String(comboCurrent);
      if (before !== next) trackedCommit(comboField.key, next);
    }
    closeCombo();
  };

  const commitChildren = async (group, entries) => {
    if (isCreate) {
      draftCommitChildren(group, entries);
      return;
    }
    // Re-keyed: refuse through the group's own channel and RE-THROW, so the
    // list keeps its inline row open (same contract as the catch below).
    if (recordGone) {
      (group === 'part_numbers' ? setPnError : setAtError)(
        'This record just synced — reopen the site to edit it.',
      );
      throw new Error('record re-keyed under the open modal');
    }
    // ONE chokepoint: every child write (PN/AT × add/edit/delete) routes
    // through here, so the catch lives here and nowhere else.
    // updateChildSet re-throws after logging (fieldStore.js:274-276) and
    // nothing above this used to catch, so a Dexie failure was an UNHANDLED
    // REJECTION — console only, no row committed, nothing on screen, and the
    // setSaveState(null) below never reached. A failed write wearing the face
    // of a no-op is the same class of lie F-44b closed on the Save path.
    // The group's OWN error channel, so the message lands beside the list it
    // belongs to rather than in the footer.
    // RE-THROW is load-bearing: PartNumbersList/AssetTagsList saveEdit catch
    // to keep the inline row open on failure (their `catch` comments say so).
    // Swallowing here would close that row as if the edit had landed.
    try {
      const updated = await updateChildSet(activeRecord.record_id, group, entries);
      if (updated && onRecordChange) onRecordChange(updated);
      setSaveState(null);
    } catch (err) {
      console.error('[fieldModal] child commit failed:', group, err);
      (group === 'part_numbers' ? setPnError : setAtError)(
        'Could not save that change on this device. Nothing was lost — try again.',
      );
      throw err;
    }
  };

  /* ── PN handlers (shape map: {value,type} row ↔ {part_number,part_number_type} list) ── */
  const pns = mergedChildren(activeRecord, 'part_numbers');
  const pnListRows = pns.map((e) => ({
    id: childKey(e),
    part_number: e.value,
    part_number_type: e.type ?? null,
    part_description: e.part_description ?? null,
  }));

  const handlePnAdd = async () => {
    const value = newPn.trim(); // identifier — trim only, never uppercase
    if (!value) return;
    if (hasDupeValue(pns, value)) {
      setPnError(`"${value}" is already on this tool.`);
      // The dupe path returns BEFORE commitChildren, so its setSaveState(null)
      // never runs and a prior F-44b refusal keeps rendering "Add or clear the
      // part number or asset tag first" in the footer — beside a dupe error
      // that says the opposite. Clear it here: the refusal's cause is being
      // reported, just not resolved.
      setSaveState(null);
      return;
    }
    setPnError(null);
    await commitChildren('part_numbers', [...pns, {
      id: null,                       // new → builder omits id → server mints
      local_key: crypto.randomUUID(), // client-only; PICKED payload never carries it
      value,
      type: newPnType || 'TBD',       // TBD-at-capture default
      ...(newPnDesc.trim() ? { part_description: newPnDesc.trim() } : {}),
    }]);
    setNewPn(''); setNewPnType('TBD'); setNewPnDesc('');
  };

  const handlePnEdit = async (key, { part_number, part_number_type, part_description }) => {
    const value = (part_number || '').trim();
    if (!value) return;
    if (hasDupeValue(pns, value, key)) {
      setPnError(`"${value}" is already on this tool.`);
      throw new Error('duplicate'); // list stays in edit mode
    }
    setPnError(null);
    await commitChildren('part_numbers', pns.map((e) => (childKey(e) === key
      ? { ...e, value, type: part_number_type || null, part_description: part_description || null }
      : e)));
  };

  const handlePnDelete = async (key) => {
    setPnError(null);
    await commitChildren('part_numbers', pns.filter((e) => childKey(e) !== key));
  };

  /* ── AT handlers ({value} row ↔ {tag_value} list) ── */
  const ats = mergedChildren(activeRecord, 'asset_tags');
  const atListRows = ats.map((e) => ({ id: childKey(e), tag_value: e.value }));

  const handleAtAdd = async () => {
    const value = newTag.trim();
    if (!value) return;
    if (hasDupeValue(ats, value)) {
      setAtError(`"${value}" is already on this tool.`);
      setSaveState(null); // same stale-refusal clear as handlePnAdd
      return;
    }
    setAtError(null);
    await commitChildren('asset_tags', [...ats, {
      id: null, local_key: crypto.randomUUID(), value,
    }]);
    setNewTag('');
  };

  const handleAtEdit = async (key, { tag_value }) => {
    const value = (tag_value || '').trim();
    if (!value) return;
    if (hasDupeValue(ats, value, key)) {
      setAtError(`"${value}" is already on this tool.`);
      throw new Error('duplicate');
    }
    setAtError(null);
    await commitChildren('asset_tags', ats.map((e) => (childKey(e) === key ? { ...e, value } : e)));
  };

  const handleAtDelete = async (key) => {
    setAtError(null);
    await commitChildren('asset_tags', ats.filter((e) => childKey(e) !== key));
  };

  // J2b: is there child work the payload would NOT carry? Two sources — a
  // list row open for inline edit (reported up by onEditingChange, since the
  // components keep the typed value private), and a typed-but-un-Added
  // add-row, whose text IS modal state.
  // newPnType is deliberately ABSENT: it initialises to 'TBD' and is reset to
  // 'TBD', never '', so including it would pin this true forever.
  const childEditPending = Boolean(
    pnEditOpen || atEditOpen || newPn.trim() || newPnDesc.trim() || newTag.trim(),
  );

  /* ── Save (D-5.2-F, edit mode): one field_capture; coalescer absorbs
     re-saves. ── */
  const handleSave = async () => {
    // Re-keyed: Save is disabled; this is the keyboard/programmatic backstop.
    if (recordGone) return;
    // F-58b: one activation, one save. See the Save button — pointer capture
    // makes pointerup fire here, and the browser may still dispatch a click
    // after it. The guard is set SYNCHRONOUSLY, before the first await, so a
    // click arriving while this is suspended is refused.
    if (saveInFlightRef.current) return;
    saveInFlightRef.current = true;
    try {
      // J2c (F-44b): refuse rather than omit. An uncommitted child edit is
      // invisible to the payload builder: PartNumbersList/AssetTagsList hold
      // the typed value in their own state and commit only on an explicit
      // tap, and there is no onBlur anywhere in either — so the blur below
      // flushes scalars and nothing else. Building the payload anyway reports
      // "Queued for sync" over a diff the rep can see is missing.
      // BEFORE the blur, and inside the try so the finally releases the
      // in-flight ref on this path too.
      if (childEditPending) {
        setSaveState('blocked');
        return;
      }

      // F-58: the blur that flushes a focused editor happens HERE, not on
      // the Save button's pointerdown. blur() dispatches its event
      // SYNCHRONOUSLY, so TextEditor's onBlur has already run — and has
      // already stored its write in pendingCommitRef — by the time the next
      // line awaits it. See the Save button for why pointerdown was the
      // wrong place to do this.
      document.activeElement?.blur();

      // F-44, part 1: await that commit, plus any commit already in flight
      // from an earlier blur. Without this the just-typed value is still
      // mid-write and its `dirty` key does not exist yet, so
      // buildCapturePayload sees an empty diff, returns null, and Save
      // reports 'No changes to send' while dropping the edit.
      await pendingCommitRef.current;

      // F-44, part 2: read the row from Dexie rather than trusting
      // activeRecord. activeRecord derives from a useLiveQuery result, so
      // catching up needs an observer fire PLUS a React re-render — neither
      // of which Save can wait for. A point-read is the only way to see the
      // write we just awaited.
      let fresh = persistedRecordId ? await getRecord(persistedRecordId) : null;
      if (!fresh) {
        // Never silent: a missing row here means the point-read disagreed
        // with what is on screen (purge, re-key, or a null id in edit mode).
        console.warn(
          `[fieldModal] Save: fresh read returned no row for record_id=${persistedRecordId} — falling back to the rendered snapshot`,
        );
        fresh = activeRecord;
      }

      // P2-5 Phase 2 (B-218): the edit path's duplicate warning — the create
      // path's rule (findToolIdMatches), non-blocking, naming the record. It
      // asks only when the rep CHANGED the TOOL ID: against the server value
      // for a synced row, against the value at open for a pending row (no
      // baseline). Warned once per value; a second Save with the same TOOL ID
      // goes through. A pending row's queued create already carries the new ID
      // (updateWorkingField rewrote it at commit), so for it the warning
      // informs rather than gates — non-blocking either way.
      const toolId = fresh.working?.supplier_tool_id;
      const toolIdBefore = isPendingRecord(fresh.record_id)
        ? toolIdAtOpenRef.current
        : fresh.baseline?.supplier_tool_id;
      const trimmed = (v) => (typeof v === 'string' ? v.trim() : v ?? '');
      if (trimmed(toolId) !== trimmed(toolIdBefore)) {
        const matches = findToolIdMatches(await getRecords(fresh.site_id), toolId,
          { excludeRecordId: fresh.record_id });
        const matchKey = toolIdMatchKey(toolId);
        if (matches.length > 0 && toolIdConfirmRef.current !== matchKey) {
          toolIdConfirmRef.current = matchKey;
          setToolIdNote({ kind: 'dupe', text: toolIdDuplicateMessage(matches, 'save') });
          setSaveState(null);
          return;
        }
      }
      setToolIdNote(null);

      const outboxId = await enqueueFieldCapture(fresh); // local IDB write

      // 2c: only a refused pending record needs to know whether its
      // tool_create exists — hasAnyToolCreate, the same status-blind test the
      // host's unsentRowState uses, so this footer and the sheet's
      // waiting/orphan split cannot disagree. One read, at the moment of
      // Save; no queue subscription. The note is CLEARED first, so nothing
      // stale shows while the read is in flight, and a failed read leaves it
      // empty (saveOutcome maps null to no outcome).
      let hasToolCreate = null;
      if (!outboxId && isPendingRecord(fresh?.record_id)) {
        setSaveState(null);
        try {
          hasToolCreate = hasAnyToolCreate(await getQueue(), fresh.record_id);
        } catch (readErr) {
          console.error('[fieldModal] Save: queue read failed — no outcome shown:', readErr);
        }
      }
      const photosQueued = photosQueuedRef.current.size;
      setSavedPhotoCount(photosQueued);
      setSaveState(saveOutcome(outboxId, fresh, photosQueued, hasToolCreate));
    } catch (err) {
      console.error('[fieldModal] save/enqueue failed:', err);
      setSaveState('error');
    } finally {
      // MANDATORY. Without it a single throw wedges Save for the life of the
      // modal — the guard above would refuse every later attempt, and the
      // catch's 'Save failed — try again' would be a lie.
      saveInFlightRef.current = false;
    }
  };

  /* ── Create (build-B-ii, D-5.2b-C/G/H): validate → mint pending row →
     enqueue tool_create → close via the SHARED onClose. Since 5.4 A-1
     Gate 3, EVERY successful enqueue (this path included) auto-triggers a
     drain cycle via the tmg-field-enqueued window event dispatched by
     fieldOutbox — the old "deliberately NO drain kick" ruling (5.2 P3
     precedent) is superseded; no explicit kick belongs HERE because the
     outbox itself nudges the drain.
     caller_email = the modal's callerEmail prop (useUser().callerEmail — the
     identical source the drain itself uses); sendToolCreate reads the drain's
     arg first, the item carry is fallback.

     RESUMABLE since the create-flush durability fix. Three phases, and the
     row on disk is the progress ledger between them:
       Phase 1 — durability: resume against a held marker, else dupe-check /
                 mint / put / mark / enqueue tool_create. Only a throw BEFORE
                 the put is a failed create.
       Phase 2 — flush: per-photo try/catch that BREAKS on the first failure;
                 each stamp persisted immediately.
       Phase 3 — assess the persisted row: all entries stamped = full success
                 and close; any left at outbox_id null = PARTIAL, which keeps
                 the marker, keeps the modal open, retains the unflushed
                 payloads, and says so with counts. ── */
  const handleCreateInner = async () => {
    // B1: the draft comes from its REF, never this render's closure. The
    // shadowing is deliberate: every draft read below — the TOOL ID gate, the
    // dupe check, the row, both enqueues, the resume branch — sees the commit
    // handleCreate's flush has just made, which this render's `draft` cannot.
    const draft = draftRef.current;
    const toolId = String(draft.working?.supplier_tool_id ?? '').trim();
    // Guards the pre-blur click race (clear the field, click before tapping
    // away: the click's blur commits null AFTER the gate last rendered) —
    // the button's disabled gate is the primary path, this is the backstop.
    if (!toolId) return;
    // D-2 RESUME BRANCH. A marker means a PRIOR invocation already persisted
    // the temp row — and almost certainly queued its tool_create — before it
    // threw. Resuming against that row is the only safe unwedge: letting the
    // retry run the normal path would mint a second pendingId, write a second
    // row and queue a second tool_create, turning one draft into two tools.
    const resume = inFlightCreateRef.current;
    let pendingId;
    let row;
    // D-5: true once the temp row is DURABLE on disk. Everything past that
    // point is resumable, so the 'Create failed' copy must not fire for it —
    // the create did not fail, it stopped part-way.
    let persisted = false;

    /* ── Phase 1: establish a durable row + a queued tool_create ────────── */
    try {
      if (resume) {
        pendingId = resume.pendingId;
        await ensureOpen();
        row = await fieldDb.records.get(pendingId);
        if (!row) {
          // Temp row already gone — reconcile re-keyed it onto the real server
          // identity and deleted it. The create COMPLETED; nothing is left to
          // resume, and re-enqueueing tool_create would mint a SECOND tool.
          // NOT a failure: close cleanly, no error copy. Any still-unflushed
          // buffer payloads are the spec's accepted residual — their row
          // entries rode onto the real row and self-heal on the next re-Load.
          inFlightCreateRef.current = null;
          setCreateError(null);
          onClose();
          return;
        }
        persisted = true; // re-read from disk — durable by definition
        setCreateError(null);
        // The dupe pre-check is DELIBERATELY skipped on resume. Its job is to
        // stop a NEW tool colliding with an existing one; here the tool
        // already exists, created by this same draft one step earlier at this
        // same record_id, so re-running it could only ever match our own row.
        // This changes WHEN the check runs, never what it permits (D-3).
        //
        // tool_create presence is DERIVED from the queue, never stored: a
        // cached boolean desyncs if notifyEnqueued throws after the syncQueue
        // put has already landed, and the queue is the only honest answer.
        const queued = await getQueue();
        const hasToolCreate = queued.some(
          (i) => i.type === 'tool_create' && i.record_id === pendingId);
        if (!hasToolCreate) {
          await enqueueToolCreate({
            record_id: pendingId,
            site_id: draft.site_id,
            supplier_tool_id: draft.working.supplier_tool_id,
            oem: null,
            caller_email: callerEmail ?? null,
          });
        }
      } else {
        // Client dupe block (D-5.2b-G client layer): same normalize as
        // hasDupeValue, against every cached record at the site — INCLUDING
        // in-flight pending-* rows (free same-rep double-create protection).
        // Only as fresh as the last Load; the server 409 is the real backstop.
        // P2-5 Phase 2: the rule now lives in data/toolIdMatch.js, shared with
        // the edit path's Save; it is the rule this block always applied
        // (trimmed, case-insensitive). The warning now names the record.
        const needle = toolIdMatchKey(toolId);
        const matches = findToolIdMatches(await getRecords(draft.site_id), toolId);
        if (matches.length > 0) {
          if (dupeConfirmId !== needle) {
            setDupeConfirmId(needle);
            setCreateError(toolIdDuplicateMessage(matches, 'create'));
            return;
          }
          // confirmed for this exact id — fall through and create
        }
        setCreateError(null);

        // Mint + write the pending row (the reconcile-expected temp shape:
        // baseline {} / etag null — reconcile reads only working/dirty/children
        // from the temp; FOUND/ADDED ride working/dirty like any key, P2-2 part C).
        // 5.3 Phase 3a (D-5.3-P3-2) flush — ORDER IS LOAD-BEARING:
        //   (b) temp row carries an optimistic media[] entry per buffered
        //       photo, object_urls MOVED forward (not revoked — they ARE the
        //       Phase 2 optimistic previews now);
        //   (c) row put BEFORE any enqueue (D-5.2b-C);
        //   (d) tool_create enqueued FIRST — FIFO-ahead of every media_add;
        //   (e) media_adds against the pending-* id (drain guard skips them
        //       until Phase 3b's D.4 re-key), each minted outbox_id stamped
        //       onto its row entry so the Phase 2 reconcile stamp can match;
        //   (f) buffer cleared per landed entry — ownership moved to the row.
        pendingId = `pending-${crypto.randomUUID()}`;
        row = {
          record_id: pendingId,
          tool_id: null,
          site_id: draft.site_id,
          etag: null,
          baseline: {},
          working: draft.working,
          dirty: draft.dirty,
          part_numbers: draft.part_numbers,
          extra_part_numbers: draft.extra_part_numbers,
          asset_tags: draft.asset_tags,
          extra_asset_tags: draft.extra_asset_tags,
          media: mediaBuffer.map((b) => ({
            outbox_id: null, // stamped right after its enqueueMediaAdd below
            pending: true,
            object_url: b.object_url,
            photo_type: b.photo_type,
            file_ext: b.file_ext,
            buffer_id: b.buffer_id, // join key for the stamp loop; inert after
          })),
          cached_at: new Date().toISOString(),
        };
        await ensureOpen();
        await fieldDb.records.put(row); // (c) row-write BEFORE enqueue (D-5.2b-C)
        persisted = true; // durable from here — no throw past this is a failure
        // D-2 marker, set the instant the row is DURABLE and BEFORE any enqueue:
        // from here on every throw is resumable, because the row a resume would
        // work against is already on disk.
        inFlightCreateRef.current = { pendingId };

        await enqueueToolCreate({      // (d) FIRST — FIFO-ahead of media_adds
          record_id: pendingId,
          site_id: draft.site_id,
          supplier_tool_id: draft.working.supplier_tool_id, // verbatim (editor already trimmed)
          oem: null, // a picked OEM is a dirty key: the post-create follow-up capture sends it
          caller_email: callerEmail ?? null,
        });
      }
    } catch (err) {
      console.error('[fieldModal] create failed:', err);
      // D-5: 'Create failed — try again' survives ONLY for a throw before the
      // row was persisted. Past that the row is on disk and the marker is set,
      // so the work is resumable — but tool_create may not have queued, so
      // this must not claim the tool is saved either.
      setCreateError(persisted
        ? 'Saved on this device — tap Create again to finish syncing.'
        : 'Create failed — try again');
      return;
    }

    /* ── Phase 2: flush the media worklist ──────────────────────────────
       The ROW is the progress ledger — an entry still at outbox_id null is
       work not yet done, first pass or resume alike. The BUFFER is the
       payload store: row entries carry object_url but never blob_data, so
       the loop drives from the buffer and joins on buffer_id. ── */
    if (mediaBuffer.length > 0) {
      for (const b of mediaBuffer) {
        // Resolve the row entry BEFORE enqueueing. A queue item carries no
        // buffer_id, so one enqueued with no row entry to stamp is
        // unattributable: a later resume cannot tell it was already sent and
        // would enqueue it again → a duplicate Media doc, permanent and
        // indistinguishable. Missing-and-resumable beats duplicated.
        const entry = (row.media || []).find((m) => m.buffer_id === b.buffer_id);
        if (!entry) continue;
        if (entry.outbox_id != null) continue; // done by an earlier pass
        try {
          const outboxId = await enqueueMediaAdd({
            record_id: pendingId,
            tool_id: pendingId, // temp id — sendMediaAdd guard skips until re-keyed
            site_id: draft.site_id,
            photo_type: b.photo_type,
            blob_data: b.blob_data,
            byte_size: b.byte_size, // D-7: real bytes, stamped at capture
            file_ext: b.file_ext,
          });
          entry.outbox_id = outboxId;
          // D-1: persist PER ITERATION, not once after the loop. Identical on
          // success; on a mid-flush throw the stamps already earned survive
          // instead of being discarded with the in-memory row, so a photo that
          // really was enqueued keeps the outbox_id its reconcile stamp matches.
          await fieldDb.records.put(row);
        } catch (err) {
          // A per-photo failure STOPS the batch but is NOT a create failure,
          // so it is caught HERE rather than propagating. BREAK, not continue:
          // the realistic thrower is storage exhaustion, which every later
          // photo would hit too. Fall through to the assessment, which reports
          // what actually landed.
          console.error('[fieldModal] media flush stopped:', err);
          break;
        }
      }
    }

    /* ── Phase 3: assess from the PERSISTED row, never the in-memory one ── */
    try {
      await ensureOpen();
      const saved = (await fieldDb.records.get(pendingId)) || row;
      const entries = saved.media || [];
      const total = entries.length;
      const remaining = entries.filter((e) => e.outbox_id == null).length;

      if (remaining === 0) {
        // Full success — outcome-equivalent to the pre-D-4 path.
        inFlightCreateRef.current = null;
        setMediaBuffer([]); // ownership moved to the row — do NOT revoke
        setCreateError(null);
        setDupeConfirmId(null);
        onRecordChange?.(saved); // optimistic: pending row into the host list
        onClose();               // shared close path — no drain trigger (ruling)
        return;
      }

      // PARTIAL (D-4/D-5/D-6): marker KEPT and modal KEPT OPEN — the user has
      // a real action to take and the retained thumbs show what remains.
      // D-4: drop ONLY the entries that actually landed, matched by buffer_id.
      // The rest keep their blob_data, which is the sole payload copy resume
      // has to work from — retaining them is load-bearing, not tidiness.
      // Object URLs: no revoke (ownership transferred to the row per stamp);
      // clearing per entry is itself what closes the duplicated-ownership
      // window, so compensating with a revoke here would blank live thumbs.
      const landed = new Set(
        entries.filter((e) => e.outbox_id != null).map((e) => e.buffer_id));
      setMediaBuffer((prev) => prev.filter((b) => !landed.has(b.buffer_id)));
      // NO onRecordChange here — deliberate, and the eyeball proved why.
      // Pushing the half-finished pending-* row into the host list makes it a
      // TAPPABLE grid row, and a tap is handleRowTap → tmgFieldModalRecordId →
      // the modal reopens in EDIT mode on a temp-id row: TOOL ID read-only,
      // Save instead of Create, and Save hits enqueueFieldCapture's D-5.2b-E
      // temp-id refusal. Worse, the next Add-tool tap runs the create-session
      // reset, which revokes the object_urls this row's entries still point
      // at — the retained payloads die and resume becomes unreachable.
      // The row is already durable in IDB; the grid learns about it on the
      // next re-Load, which is soon enough. Host push stays on the full
      // success path only, exactly as before this fix.
      setCreateError(
        `Tool saved. ${total - remaining} of ${total} photo${total === 1 ? '' : 's'} queued`
        + ' — tap Create again to finish the rest.');
    } catch (err) {
      // The assessment itself failed. The row and every stamp are still on
      // disk and the marker is intact, so this is resumable — never report a
      // failed create.
      console.error('[fieldModal] create assessment failed:', err);
      setCreateError('Tool saved — tap Create again to finish syncing its photos.');
    }
  };

  /* ── F-59a: Create's re-entry guard ──────────────────────────────────────
     A WRAPPER rather than a try/finally threaded through the body above, and
     that is the point: handleCreateInner is a three-phase resumable create
     with four separate exits (the empty-toolId bail, the already-re-keyed
     close, the Phase 1 catch's return, and falling off the end). Wrapping the
     body in place would re-indent ~230 lines of the most delicate code in this
     file for no behavioural gain; this leaves it byte-for-byte untouched and
     still clears the ref on every one of those exits, because `finally` runs
     for all of them.

     Needed because the Save button's pointer pair is now on Create too: one
     activation can deliver BOTH a pointerup and a click. See the button. ── */
  const handleCreate = async () => {
    if (createInFlightRef.current) return;
    createInFlightRef.current = true;
    try {
      // J2d: refuse before anything is minted. Create is the severe case —
      // handleCreateInner writes draft.part_numbers into the durable row and
      // queues tool_create, and the draft is component state with no Dexie
      // fallback, so an omitted child edit dies with the modal.
      if (childEditPending) {
        setCreateError("Add or clear the part number or asset tag first \u2014 it won't be saved with this tool.");
        return;
      }

      // B1 (P2-1): Save's F-58 / F-44 flush, ported. Without it the field
      // still focused when Create is tapped never reaches the row — the
      // 2026-10-01 TEST witness lost ESTIMATED WEIGHT exactly this way.
      // HERE, not on the button's pointerdown, for F-58's reason: Create is
      // the same footer, and blurring mid-gesture dismisses the keyboard and
      // moves the button under the finger. blur() dispatches synchronously,
      // so a focused TextEditor's onBlur has committed into draftRef by the
      // next line. If the input already blurred before this handler ran, the
      // blur is a no-op and the ref is what carries that commit instead.
      // The await mirrors Save; a create-mode commit settles at once today.
      document.activeElement?.blur();
      await pendingCommitRef.current;

      await handleCreateInner();
    } finally {
      // MANDATORY, exactly as on Save: without it one throw wedges Create for
      // the life of the modal and 'Create failed — try again' becomes a lie.
      createInFlightRef.current = false;
    }
  };

  const editorFor = (field) => {
    if (field.kind === 'bool') return <BoolEditor field={field} record={activeRecord} onCommit={trackedCommit} />;
    if (field.kind === 'enum') return <EnumEditor field={field} record={activeRecord} onOpenSheet={openSheet} />;
    // Wave 2 item 5: EnumEditor is reused as the per-row TRIGGER only — a
    // button showing the current value that opens a sheet. It opens the
    // location picker's sentinel, not the enum sheet's.
    if (LOCATION_COMBO_KEYS.has(field.key)) {
      return <EnumEditor field={field} record={activeRecord} onOpenSheet={openCombo} />;
    }
    return <TextEditor field={field} record={activeRecord} onCommit={trackedCommit} />;
  };

  /* ── Unit 4 Phase 3: the row renderers, EXTRACTED not duplicated ─────────
     Both bands — the main body and the Hidden Fields drawer — render through
     these two functions. Extracting them was the point: a second copy of the
     editable row is a second call site for editorFor, and the whole reason
     F-44 holds is that EVERY scalar commit routes through trackedCommit and
     lands in pendingCommitRef. A forked row would escape that silently and
     Save would stop awaiting the commit it needs. One definition means the
     drawer cannot drift from the body — not by styling, not by test id, and
     above all not by commit path. */
  /* MANIFEST-DRIVEN DISPLAY FORMAT, not a key branch (Unit F, 2026-09-04).
     CALCULATED WEIGHT reads as a bare 6-digit run without separators, which
     is the field a rep is most likely to misread by an order of magnitude.
     The formatting decision therefore lives on the manifest entry
     (`format: 'comma'`, editableFields.js) and this renderer just honours it.

     WHY NOT `if (f.key === 'calculated_weight')` HERE: readonlyRow is the
     single renderer for all four read-only rows, in the main body and in the
     hidden-fields drawer alike. A key branch inside it would make the
     renderer carry per-field knowledge, and the next field wanting a format
     would add a second branch rather than a manifest prop. One `format`
     switch scales; a chain of key tests does not.

     NUMERIC GUARD IS DELIBERATE. Number('') is 0 and Number(null) is 0, so
     the empty test below has to run FIRST — it does, unchanged. Beyond that,
     a legacy free-text weight ('2 sets') yields NaN, fails isFinite, and
     falls through to String(v) rather than rendering 'NaN'.

     formatReadonly itself now lives in data/formatReadonly.js (Bob P1 item
     2a), unchanged, so the records grid formats a column exactly as this
     row does. */
  const readonlyRow = (f) => {
    const v = activeRecord.working?.[f.key];
    return (
      <div className="frm-field" key={f.key}>
        <span className="frm-label">{f.label}</span>
        <span className="frm-value" data-testid={`frm-readonly-${f.key}`}>
          {v === null || v === undefined || v === '' ? '—' : formatReadonly(f, v)}
        </span>
      </div>
    );
  };

  /* Item 8 (Bob, gloves): a bool's checkbox sits INSIDE its <label>, box
     first, then the text — the normal reading order for a checkbox — so the
     whole strip toggles it and the 24px box is no longer the only target.
     htmlFor + id are kept although nesting alone would associate them: belt
     and braces, and the tests pin the explicit pairing. Every other kind keeps
     <span> then editor, unchanged — those editors carry no id, so a <label>
     there would bind to nothing.
     No bool carries `provenance` today (material, an enum, is the only one),
     so the chip below never renders in the bool branch. It stays in the
     shared content so a future bool with provenance renders it as one more
     row item after the text rather than losing it silently. */
  const editableRow = (field) => {
    const labelContent = (
      <>
        {field.label}
        {field.provenance && materialSource && (
          <span
            className={`frm-prov-chip frm-prov-${String(materialSource).toLowerCase()}`}
            data-testid="frm-material-provenance"
          >
            {String(materialSource)}
          </span>
        )}
      </>
    );
    if (field.kind === 'bool') {
      return (
        <div className="frm-field" key={field.key}>
          <label className="frm-label frm-label--bool" htmlFor={boolInputId(field.key)}>
            {editorFor(field)}
            {labelContent}
          </label>
        </div>
      );
    }
    return (
      <div className="frm-field" key={field.key}>
        <span className="frm-label">{labelContent}</span>
        {editorFor(field)}
      </div>
    );
  };

  // One list, two renderers: picked by MEMBERSHIP in READONLY_DISPLAY_KEYS, as
  // the list was split before it became one. A read-only field renders through
  // readonlyRow wherever it sits — never as a disabled editor.
  // P2-5 Phase 2 (B-218): TOOL ID is the one exception, and only in THIS form.
  // It stays in READONLY_DISPLAY_FIELDS, so the grid, search, sort and the
  // hide lock treat it exactly as before; edit mode renders it as an editor in
  // its manifest position. Create mode never reaches this branch for it (its
  // editor leads the form, and editableFields drops read-only keys). The
  // Fragment key carries toolIdEditorKey so a refused blank remounts the editor
  // on the stored value.
  const fieldRow = (f) => {
    if (!isCreate && f.key === TOOL_ID_FIELD.key) {
      return <React.Fragment key={`${f.key}-${toolIdEditorKey}`}>{editableRow(TOOL_ID_FIELD)}</React.Fragment>;
    }
    return READONLY_DISPLAY_KEYS.has(f.key) ? readonlyRow(f) : editableRow(f);
  };

  // ── Discard (field cancel Phase 2) ──────────────────────────────────────
  // Rendered ONLY in edit mode on a not-yet-re-keyed row. Absent — not
  // disabled — on a synced row: server-side delete is tabled with no clearing
  // condition, so a permanently disabled control there would be a dead end.
  // Ignores childEditPending on purpose: handleCreate refuses while a child
  // edit is open because an uncommitted child would be silently dropped;
  // Discard drops everything by design.
  //
  // Reads the LIVE row, never the prop fallback (re-key fix). activeRecord
  // falls back to the prop snapshot when the row is gone, and that snapshot
  // still carries the pending- id after a re-key — so Discard kept rendering
  // on a record the chip already called Synced. No Discard until the store
  // has confirmed the row: the one-frame delay before the first read lands is
  // correct, not a flicker to smooth over.
  const discardRecordId = !isCreate ? liveRow?.record_id ?? null : null;
  const showDiscard = !isCreate && liveRow != null && isPendingRecord(liveRow.record_id);

  // Two sensors, two jobs (2B): `syncing` (the host's one useFieldDrain
  // instance) drives the DISABLED state — it re-renders but lags a cycle by
  // one render. isFieldDrainActive() inside discardPendingRecord drives the
  // REFUSAL — current, but a module read cannot re-render anything.
  const handleDiscardTap = async () => {
    if (syncing || discardTapInFlightRef.current) return;
    discardTapInFlightRef.current = true;
    setDiscardNote(null);
    try {
      // Preflight BEFORE the dialog opens: a record whose tool_create the
      // server has already accepted must never reach the confirm dialog.
      const pre = await getDiscardPreflight(discardRecordId);
      setDiscardError(null);
      setDiscardFutile(false);
      setDiscardDialog(pre.blocked === 'already_created'
        ? { kind: 'blocked' }
        : { kind: 'confirm', photoCount: pre.photoCount });
    } catch (err) {
      console.error('[fieldModal] discard preflight failed:', err);
      setDiscardNote("Couldn't check this record — try again.");
    } finally {
      discardTapInFlightRef.current = false;
    }
  };

  const handleDiscardConfirmed = async () => {
    if (discarding) return;
    setDiscarding(true);
    setDiscardError(null);
    try {
      const res = await discardPendingRecord(discardRecordId);
      if (res.removed) {
        setDiscardSucceeded(true);
        setDiscardDialog(null);
        // Host pops the modal sentinel FIRST, then drops the row from its
        // one-shot records snapshot (no ghost row behind the closed modal).
        if (onRecordRemoved) onRecordRemoved(discardRecordId);
        return;
      }
      // Refusals: the confirm must NOT close as though it succeeded.
      switch (res.reason) {
        case 'already_created':
          // The preflight raced a drain that marked the create saved.
          setDiscardDialog({ kind: 'blocked' });
          break;
        case 'drain_active':
          setDiscardError('A sync started, so nothing was discarded. Try again when it finishes.');
          break;
        case 'row_absent':
          setDiscardFutile(true);
          setDiscardError('This record is no longer on this device. Nothing was discarded.');
          break;
        case 'not_pending':
          setDiscardFutile(true);
          setDiscardError('This record has already synced, so it cannot be discarded here. Nothing was discarded.');
          break;
        default:
          setDiscardError(`Nothing was discarded (${res.reason || 'unknown reason'}).`);
      }
    } catch (err) {
      // A throw inside the transaction rolls the whole thing back.
      console.error('[fieldModal] discard failed:', err);
      setDiscardError("Couldn't discard — nothing was changed. Try again.");
    } finally {
      setDiscarding(false);
    }
  };

  const closeDiscardDialog = () => {
    if (!discarding) setDiscardDialog(null);
  };

  const discardPhotoCount = discardDialog?.kind === 'confirm' ? discardDialog.photoCount : 0;

  // ── P2-5 Phase 3 (B-198): the record menu ───────────────────────────────
  // Three dots at the header's top right, Bob's reference build. DUPLICATE AND
  // CANCEL ONLY: delete is parked behind B-097's eviction ruling (B-014,
  // B-146), decided 2026-10-22 with Bob told ahead of the build. Edit mode
  // only, and only when the host wires onDuplicate; a record that is gone
  // (re-keyed under the open form) offers nothing.
  const handleDuplicateTap = () => {
    setMenuOpen(false);
    onDuplicate(activeRecord);
  };
  const recordMenu = !isCreate && typeof onDuplicate === 'function' && !recordGone ? (
    <div className="frm-menu-anchor">
      <button
        type="button"
        className="frm-menu-btn"
        aria-label="Record actions"
        aria-haspopup="menu"
        aria-expanded={menuOpen}
        data-testid="frm-menu-btn"
        onClick={() => setMenuOpen((o) => !o)}
      >
        &#8942;
      </button>
      {menuOpen && (
        <div className="frm-menu" role="menu" data-testid="frm-menu">
          <button type="button" role="menuitem" className="frm-menu-item"
            data-testid="frm-menu-duplicate" onClick={handleDuplicateTap}>
            Duplicate
          </button>
          <button type="button" role="menuitem" className="frm-menu-item"
            data-testid="frm-menu-cancel" onClick={() => setMenuOpen(false)}>
            Cancel
          </button>
        </div>
      )}
    </div>
  ) : null;

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      className="frm-dialog"
      headerActions={recordMenu}
      footer={(
        <div className="frm-footer">
          {/* Discard's disabled reason owns the note slot while a cycle runs.
              It displaces only the two SUCCESS notes (a queued capture is
              exactly what is syncing); the error notes below still render. */}
          {showDiscard && syncing && (
            <span className="frm-save-note frm-save-note-muted" data-testid="frm-discard-syncing">
              Syncing — try again shortly
            </span>
          )}
          {showDiscard && discardNote && (
            <span className="frm-save-note frm-save-note-error" data-testid="frm-discard-note">
              {discardNote}
            </span>
          )}
          {/* B4 (re-key fix): a scalar write that failed. Footer note slot, the
              modal's one channel for write outcomes (Save's queued/noop/error
              already live here). Short on purpose — one row, the footer
              height is what --frm-vv-height assumes. Suppressed when the
              record is gone: the notice above the fields owns that story. */}
          {!isCreate && commitError && !recordGone && (
            <span className="frm-save-note frm-save-note-error" data-testid="frm-commit-error">
              {commitError} didn&apos;t save — try again.
            </span>
          )}
          {/* P2-5: TOOL ID's own note — a duplicate warning naming the record
              (a second Save keeps the ID) or a refused blank. */}
          {!isCreate && toolIdNote && !recordGone && (
            <span className="frm-save-note frm-save-note-error" data-testid={`frm-tool-id-${toolIdNote.kind}`}>
              {toolIdNote.text}
            </span>
          )}
          {!isCreate && saveState === 'queued' && !(showDiscard && syncing) && (
            <span className="frm-save-note" data-testid="frm-save-note">Queued for sync ✓</span>
          )}
          {/* B5: two more SUCCESS notes, displaced by Discard's syncing note
              exactly like the two above. 'held' is a pending record's Save:
              refused by design, but its edits are kept and go up with the
              tool. 'photos' is a photo-only Save: the photo did save. */}
          {!isCreate && saveState === 'held' && !(showDiscard && syncing) && (
            <span className="frm-save-note" data-testid="frm-save-note">Saved on this device — sends with the new tool</span>
          )}
          {!isCreate && saveState === 'photos' && !(showDiscard && syncing) && (
            <span className="frm-save-note" data-testid="frm-save-note">
              {savedPhotoCount === 1 ? 'Photo' : 'Photos'} saved ✓ — no other changes
            </span>
          )}
          {!isCreate && saveState === 'noop' && !(showDiscard && syncing) && (
            <span className="frm-save-note" data-testid="frm-save-note">No changes to send</span>
          )}
          {!isCreate && saveState === 'unsent' && (
            /* 2c: the one Save outcome that asks the rep to ACT, so it takes
               the error styling and is NOT displaced by Discard's syncing note
               (an orphan is exactly a row Discard renders on). Same words as
               the sheet's orphan row ("not sent yet — tap Send"), so the two
               surfaces agree. */
            <span className="frm-save-note frm-save-note-error" data-testid="frm-save-note">
              Saved on this device — tool not sent yet. Tap Send in Sync status
            </span>
          )}
          {!isCreate && saveState === 'error' && (
            <span className="frm-save-note frm-save-note-error" data-testid="frm-save-note">Save failed — try again</span>
          )}
          {!isCreate && saveState === 'blocked' && childEditPending && (
            /* J2f: the `&& childEditPending` conjunct is what clears this. The
               existing setSaveState(null) sites cover a COMMITTED child edit
               (commitChildren, edit mode) and any scalar commit
               (handleCommit), but neither fires when the rep taps Cancel on
               the list or simply empties the add-row field — and a refusal
               that outlives its cause is the same class of lie as the
               omission it replaced. Gating the render on the live condition
               covers every exit without a new effect. */
            <span className="frm-save-note frm-save-note-error" data-testid="frm-save-note">
              Add or clear the part number or asset tag first.
            </span>
          )}
          {isCreate && createError && (
            <span className="frm-save-note frm-save-note-error" data-testid="frm-create-error">
              {createError}
            </span>
          )}
          {showDiscard && (
            // Leftmost of the three, content width (not flex: 1) so Save and
            // Close keep their sizing and the footer stays ONE row — its
            // height is what the --frm-vv-height geometry assumes. Danger
            // OUTLINE: the filled red is the confirm's commit button, and two
            // filled reds in sequence read as the same control.
            //
            // Pointer capture per F-58b / F-59a: this footer travels when the
            // keyboard dismisses, same as Save's. onClick stays ungated for
            // keyboard/assistive activation; discardTapInFlightRef absorbs the
            // pointerup+click pair.
            <button
              type="button"
              className="frm-discard-btn"
              data-testid="frm-discard-btn"
              disabled={syncing}
              onPointerDown={(e) => {
                // Throws if the pointer is already released; nothing to do then.
                try { e.currentTarget.setPointerCapture(e.pointerId); } catch {}
              }}
              onPointerUp={(e) => {
                try { e.currentTarget.releasePointerCapture(e.pointerId); } catch {}
                handleDiscardTap();
              }}
              onClick={handleDiscardTap}
            >
              Discard
            </button>
          )}
          {isCreate ? (
            // D-5.2b-H reactive required rule: enabled only when the draft
            // TOOL ID has non-whitespace content (blur-committed).
            //
            // F-59a: pointer capture, mirroring Save. This footer's geometry is
            // --frm-vv-height, so dismissing the keyboard grows the dialog and
            // travels this button hundreds of pixels DOWN between pointerdown
            // and liftoff; capture retargets pointerup here wherever layout put
            // it. onClick stays ungated for keyboard/programmatic/assistive
            // activation, and handleCreate's in-flight ref absorbs the
            // pointerup+click pair on the path where nothing moves.
            //
            // ⚠ Capture does NOT help the empty→type→tap case: while the typed
            // TOOL ID is still un-blurred the draft is empty, so this button is
            // DISABLED and dispatches no pointer events at all. That is a
            // separate defect from F-58 and is NOT fixed here.
            <button
              type="button"
              className="frm-save-btn"
              data-testid="frm-create-btn"
              onPointerDown={(e) => {
                // Throws if the pointer is already released; nothing to do then.
                try { e.currentTarget.setPointerCapture(e.pointerId); } catch {}
              }}
              onPointerUp={(e) => {
                try { e.currentTarget.releasePointerCapture(e.pointerId); } catch {}
                handleCreate();
              }}
              onClick={handleCreate}
              disabled={!String(draft.working?.supplier_tool_id ?? '').trim()}
            >
              Create
            </button>
          ) : (
            // F-58: the blur that flushes a focused editor is NOT on
            // pointerdown any more — it moved into handleSave. Doing it here
            // dismissed the keyboard MID-GESTURE, the visualViewport resize
            // grew --frm-vv-height, and the fixed dialog and this footer
            // repositioned un-animated in the same frame.
            //
            // F-58b: the footer moves anyway. iOS dismisses the keyboard when
            // Save is TOUCHED, so the dialog grows back to full height and
            // this footer travels several hundred pixels DOWN between
            // pointerdown and liftoff — pointerdown hit the button, pointerup
            // hit empty space, and no click was dispatched at all. Pointer
            // CAPTURE retargets every later event for this pointer to the
            // button no matter where layout put it, so pointerup still lands
            // here and activates Save.
            //
            // onClick is deliberately UNGATED: keyboard, programmatic and
            // assistive activation dispatch a click with no pointer sequence
            // at all, and gating it would make Save dead on those paths. When
            // the button does NOT move, one activation therefore delivers both
            // a pointerup and a click — handleSave's own in-flight ref is what
            // makes the second harmless, not a guard spread across handlers.
            <button
              type="button"
              className="frm-save-btn"
              data-testid="frm-save-btn"
              disabled={recordGone}
              onPointerDown={(e) => {
                // Throws if the pointer is already released; nothing to do then.
                try { e.currentTarget.setPointerCapture(e.pointerId); } catch {}
              }}
              onPointerUp={(e) => {
                try { e.currentTarget.releasePointerCapture(e.pointerId); } catch {}
                handleSave();
              }}
              onClick={handleSave}
            >
              Save
            </button>
          )}
          <button type="button" className="frm-close-btn" onClick={onClose}>
            Close
          </button>
        </div>
      )}
    >
      {/* Keyed per identity: a fresh key per create session (and per record in
          edit mode) remounts the editor subtree, so editors' local input state
          re-seeds from the fresh draft/row. Key is stable while typing. */}
      {/* Re-keyed notice. STICKY at the top of the scrolling body, not in
          the footer: it is several lines, and the footer must stay one row
          for the --frm-vv-height geometry. Sticky keeps it on screen wherever
          the rep had scrolled to when their input stopped landing. */}
      {recordGone && (
        <div className="frm-rekeyed-notice" role="status" data-testid="frm-rekeyed-notice">
          <strong>This record just synced, so an edit still in progress was
          not saved.</strong> The record is safe on the server with its
          permanent ID, and everything saved before the sync is kept. This
          screen can&apos;t make changes to it any more — close it and reopen
          the site to keep editing.
        </div>
      )}
      <React.Fragment key={isCreate ? `create-${createSession}` : `edit-${activeRecord.record_id}`}>
      {/* One native lock for every control in the body — text and checkbox
          editors, enum triggers, the gallery's capture
          input and thumbnails, the PN/AT lists. A disabled <fieldset>
          disables all descendant form controls, so no editor needs its own
          prop. The guards in handleCommit / commitChildren / handleMediaDelete
          / handleSave cover what is outside it or already in flight. */}
      <fieldset className="frm-body-lock" disabled={recordGone}>
      <div className="frm-fields" data-testid="field-record-modal">
        {/* Create mode: TOOL ID leads the form (D-5.2b-H — required at Save,
            validated in build-B-ii). Create mode has no read-only rows; MEDIA
            is pinned first, directly under this editor. */}
        {isCreate && (
          <div className="frm-field" key={TOOL_ID_FIELD.key}>
            <span className="frm-label">{TOOL_ID_FIELD.label}</span>
            {/* F-59b: commitOnChange HERE and nowhere else. Create mode's
                commit sink is draftCommitScalar — an in-memory setDraft, no
                Dexie write, no outbox, no network — so per-keystroke is cheap.
                The Create button's disabled gate reads this same
                draft.working.supplier_tool_id, and committing as the rep types
                is what makes that gate LIVE: without it the gate lags the
                typed value by one blur, so the first tap on Create only
                dismissed the keyboard and a second was needed.

                Opt-in rather than TextEditor's default because EDIT mode's
                sink is updateWorkingField, a Dexie write — one per keystroke
                on every text field is not a trade worth making, and editorFor
                passes nothing so it keeps blur-only commit. */}
            <TextEditor
              field={TOOL_ID_FIELD}
              record={activeRecord}
              onCommit={trackedCommit}
              commitOnChange
              focusOnMount={duplicateSession}
            />
          </div>
        )}

        {/* NO INV/ALL SWITCH HERE (Bob P1 item 2a, fault 2). It moved to the
            records grid's header: "INV and ALL switch from the grid header,
            not from inside a record or the New Tool form." The VIEW still
            governs this form — `fieldView` arrives as a prop and drives the
            view filter, the read-only rows and the PN/AT section exactly as
            before; only the control that set it from in here is gone. */}

        {/* The view's rows in one sequence, cut once at MEDIA's slot (mediaAt
            above). Create mode's cut is at 0, so everything follows MEDIA. */}
        {visibleBeforeMedia.map(fieldRow)}
        {/* 5.3 Phase 2: edit-mode capture gallery. Phase 3a (D-5.3-P3-1):
            create mode mounts the SAME gallery in buffer mode — captures go
            to the draft-local buffer (no enqueue until Create flushes). */}
        <div className="frm-field frm-field-media">
          <span className="frm-label">MEDIA</span>
          {isCreate ? (
            <FieldMediaGallery
              record={{ ...draft, media: mediaBuffer }}
              onOpenViewer={openViewer}
              onBufferCapture={handleBufferCapture}
            />
          ) : (
            <FieldMediaGallery
              record={activeRecord}
              onRecordChange={handleGalleryRecordChange}
              onOpenViewer={openViewer}
            />
          )}
        </div>

        {visibleAfterMedia.map(fieldRow)}

        {/* ── Hidden Fields drawer (Unit 4 Phase 3) ─────────────────────────
            Below the last main-body row, above PART NUMBERS / ASSET TAGS.
            Rows here are FULLY EDITABLE and go through the same two renderers
            the body above uses, so the same editors and the same
            trackedCommit sink — hiding relocates a field, it never demotes
            it. Same list, same order as the body: read-only rows sit among
            the editors here too.

            Renders only when something is actually hidden, so a rep who has
            hidden nothing never sees an empty drawer. */}
        {hiddenCount > 0 && (
          <div className="frm-hidden" data-testid="frm-hidden-section">
            <button
              type="button"
              className="frm-hidden-toggle"
              data-testid="frm-hidden-toggle"
              aria-expanded={hiddenOpen}
              onClick={() => setHiddenOpen((open) => !open)}
            >
              <span className="frm-hidden-title">Hidden Fields ({hiddenCount})</span>
              <svg
                className={`frm-hidden-chevron${hiddenOpen ? ' frm-hidden-chevron-open' : ''}`}
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M4 6l4 4 4-4" />
              </svg>
            </button>
            {hiddenOpen && (
              <div className="frm-hidden-body" data-testid="frm-hidden-body">
                {hiddenFields.map(fieldRow)}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Phase 3: PN/AT (components verbatim; field-zone handlers) ── */}
      {/* ALL only. The child sets are not part of the rack-walk pass, and
          gating them here is presentation only — a pending child edit lives
          in the record's arrays with a dirty sentinel and still ships on
          Save whichever view is active when the rep taps it. */}
      {isAllView && (
      <>
      <div className="frm-children" data-testid="frm-part-numbers">
        <h3 className="frm-children-title">PART NUMBERS</h3>
        <PartNumbersList
          partNumbers={pnListRows}
          error={pnError}
          onAdd={handlePnAdd}
          onEdit={handlePnEdit}
          onDelete={handlePnDelete}
          newPn={newPn} setNewPn={setNewPn}
          newPnType={newPnType} setNewPnType={setNewPnType}
          newPnDesc={newPnDesc} setNewPnDesc={setNewPnDesc}
          partNumberTypeOptions={options.part_number_type}
          showTypeControl={false}
          onEditingChange={setPnEditOpen}
          /* F-58, and this zone ONLY. These two child lists live inside
             .frm-dialog, whose height below 1024px IS --frm-vv-height — so
             dismissing the keyboard grows the dialog and travels every
             button in the scroller down between pointerdown and liftoff.
             Witnessed on TEST 8f514f5, 2026-09-02, iOS handset: keyboard UP,
             tapping Add committed nothing and reported nothing; keyboard
             DOWN, the same tap worked. The four legacy call sites must NOT
             pass this — their onAdd/onEdit POST a mint, and the ungated
             onClick that rides alongside capture would double-fire them into
             duplicate documents. See the prop contract in PartNumbersList. */
          pointerCapture
        />
      </div>
      <div className="frm-children" data-testid="frm-asset-tags">
        <h3 className="frm-children-title">ASSET TAGS</h3>
        <AssetTagsList
          assetTags={atListRows}
          error={atError}
          onAdd={handleAtAdd}
          onEdit={handleAtEdit}
          onDelete={handleAtDelete}
          newTag={newTag} setNewTag={setNewTag}
          onEditingChange={setAtEditOpen}
          /* F-58 — same dialog, same vv-height geometry, same opt-in. Not
             separately witnessed on the asset-tag row, but it is the same
             button shape in the same moving scroller; leaving it off would
             be a knowingly half-fixed defect. */
          pointerCapture
        />
      </div>
      </>
      )}
      </fieldset>
      </React.Fragment>

      {/* ── 5.3 Phase 1: the ONE BottomSheetSelect instance (D-5.3-8) —
          outside the keyed fragment (a create-session remount must not kill
          an open sheet), inside ModalShell (unmounts with the modal). ── */}
      <BottomSheetSelect
        isOpen={isOpen && sheetField != null}
        label={sheetField?.label ?? ''}
        value={sheetCurrent}
        options={sheetField ? (rankedEnumOptions ?? (options[sheetField.refKey] || [])) : []}
        onSelect={handleSheetSelect}
        onClose={closeSheet}
        offListCurrent={sheetField?.offListCurrent}
      />

      {/* ── Wave 2 item 5: the ONE FieldComboSheet instance — own sentinel
          (tmgFieldComboField), same placement rationale as the sheet above:
          outside the keyed fragment, inside ModalShell. ── */}
      <FieldComboSheet
        isOpen={isOpen && comboField != null}
        label={comboField?.label ?? ''}
        fieldKey={comboField?.key ?? null}
        value={comboCurrent}
        records={records}
        onSelect={handleComboSelect}
        onClose={closeCombo}
      />

      {/* ── 5.3 Phase 2: the ONE viewer instance — own sentinel, same
          outside-the-keyed-fragment placement rationale as the sheet.
          Phase 3a: create mode views the draft buffer (delete = buffer
          drop via handleMediaDelete's create arm). ── */}
      <FieldMediaViewer
        isOpen={isOpen && viewerIdx != null
          && (isCreate ? mediaBuffer.length : (activeRecord.media?.length ?? 0)) > 0}
        media={isCreate ? mediaBuffer : (activeRecord.media || [])}
        initialIndex={viewerIdx ?? 0}
        onClose={closeViewer}
        onDelete={handleMediaDelete}
      />

      {/* ── Field cancel Phase 2: the Discard dialog. FieldMediaViewer's
          confirm pattern — local state, backdrop click / Cancel / Escape
          (capture-phase, above). Fixed-position inside ModalShell's backdrop
          stacking context, so it only has to outrank its siblings. ── */}
      {isOpen && discardDialog && (
        <div className="frm-confirm-backdrop" onClick={closeDiscardDialog}>
          <div
            className="frm-confirm-box"
            role="alertdialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
            data-testid="frm-discard-dialog"
          >
            {discardDialog.kind === 'blocked' ? (
              <>
                <div className="frm-confirm-title">Already sent to the server</div>
                <div className="frm-confirm-label">{recordLabel(activeRecord)}</div>
                <div className="frm-confirm-body">
                  The server has already accepted this tool, so it can&apos;t be
                  discarded from this device. It will reappear as a normal
                  record the next time this site is loaded.
                </div>
                <div className="frm-confirm-btns">
                  <button type="button" className="frm-confirm-cancel" onClick={closeDiscardDialog}>
                    OK
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="frm-confirm-title">Discard this record?</div>
                <div className="frm-confirm-label">{recordLabel(activeRecord)}</div>
                <div className="frm-confirm-body">
                  This record has not been created on the server. Discarding
                  removes it from this device
                  {discardPhotoCount > 0 && (
                    <>
                      , including{' '}
                      <strong>
                        {discardPhotoCount} {discardPhotoCount === 1 ? 'photo' : 'photos'}
                      </strong>
                      {' '}that {discardPhotoCount === 1 ? 'has' : 'have'} not been uploaded yet
                    </>
                  )}
                  . This cannot be undone.
                </div>
                {discardError && (
                  <div className="frm-confirm-error" data-testid="frm-discard-error">{discardError}</div>
                )}
                <div className="frm-confirm-btns">
                  {discardFutile ? (
                    <button type="button" className="frm-confirm-cancel" onClick={closeDiscardDialog}>
                      Close
                    </button>
                  ) : (
                    <>
                      <button
                        type="button"
                        className="frm-confirm-cancel"
                        onClick={closeDiscardDialog}
                        disabled={discarding}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="frm-confirm-commit"
                        onClick={handleDiscardConfirmed}
                        disabled={discarding}
                        data-testid="frm-discard-commit"
                      >
                        {discarding ? 'Discarding…' : 'Discard record'}
                      </button>
                    </>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </ModalShell>
  );
}
